import React, { useState, useEffect, useRef } from 'react';
import { PCInstance, ConfigurationRelease, Operator, Notification, HeadsetStatus as HeadsetStatusType, DiagnosticsStatus, SyncStatusBlock, HmdBoundaryStatus, StartedEntry } from '../types/database';
import { PCAgentClient, createPCAgentClient, HmdScreenshotResult } from '../services/pcAgent';
import { getRolePermissions } from '../services/rolePermissions';
import { databaseService } from '../services/database';
import { supabase } from '../lib/supabase';
import { wakeMachines } from '../services/wake';
import {
  dequeueForLaunch,
  displayName as queueDisplayName,
  fullName,
  isNotQueuedError,
  partyMarker,
} from '../services/queue';
import {
  crashRecoveryView,
  queueMove,
  queueRequeue,
  queueRestart,
  type TileRecoveryDeps,
} from '../services/tileRecovery';
import { useQueueDequeueOptional } from '../contexts/QueueDequeueContext';
import { useQueueDispatchOptional } from '../contexts/QueueDispatchContext';
import { useSettings } from '../contexts/SettingsContext';
import { NOTIFICATION_MAX_GAIN } from '../config/settings';
import { broadcastNextUp, type NextUpSignal } from './NextUpWidget';
import LanguageFlag, { hasLanguageFlag } from './LanguageFlag';
import MarkIssueModal, { MarkIssueSubmit } from './MarkIssueModal';
import SendVisitorMessageModal from './SendVisitorMessageModal';
import HeadsetSheet from './HeadsetSheet';
import type { CrashRecovery, HmdInfo, HmdListData } from '../services/pcAgent';
import { OperatorMessage } from '../services/operatorMessages';
import DiagnosticsOverrideModal from './DiagnosticsOverrideModal';
import DiagnosticsReportModal from './DiagnosticsReportModal';
import Toast, { ToastData } from './Toast';

// One AudioContext for the whole page, created lazily and never closed.
//
// Browsers cap the number of concurrent AudioContexts (~6 in Chrome/Electron).
// Every beep used to construct a fresh one and never close it, so a dashboard
// full of tiles exhausted the cap after a handful of alerts; from then on
// `new AudioContext()` threw, the throw was swallowed by the caller's try/catch,
// and every later notification was silently mute. Sharing one context means the
// Nth alert of a session is as audible as the first.
let sharedAudioContext: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  try {
    if (!sharedAudioContext) {
      const Ctor = window.AudioContext || (window as any).webkitAudioContext;
      if (!Ctor) return null;
      sharedAudioContext = new Ctor();
    }
    // Autoplay policy parks a context in 'suspended' until a user gesture, and
    // it can also be suspended when a window is backgrounded.
    if (sharedAudioContext.state === 'suspended') {
      void sharedAudioContext.resume();
    }
    return sharedAudioContext;
  } catch (error) {
    console.error('Failed to create shared AudioContext:', error);
    return null;
  }
}

// Play the two-tone alert beep. `volumePercent` (0–100) is a device-local
// setting; it maps linearly onto 0..NOTIFICATION_MAX_GAIN. A non-positive
// volume is a no-op (the caller normally gates on the enabled flag first).
//
// Module-level rather than per-component so the shared alarm below can drive it
// without belonging to any one tile. Exported for the staging FakePCTile's
// nearly-done chime.
export function playDangerAlert(volumePercent: number) {
  const gain = (Math.max(0, Math.min(100, volumePercent)) / 100) * NOTIFICATION_MAX_GAIN;
  if (gain <= 0) return;
  const audioContext = getAudioContext();
  if (!audioContext) return;
  try {
    // Both tones are scheduled on the audio clock rather than via setTimeout,
    // so the pair stays tight even when the main thread is busy rendering.
    const now = audioContext.currentTime;
    for (const offset of [0, 0.3]) {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.value = gain;
      oscillator.start(now + offset);
      oscillator.stop(now + offset + 0.2);
    }
  } catch (error) {
    console.error('Failed to play danger alert:', error);
  }
}

// ---------------------------------------------------------------------------
// Shared repeating alarm
//
// Repeating notifications (visitor_done, crash/freeze/GPU recovery) used to each
// run their own interval inside their own tile, so two alerting PCs meant two
// alarms beating against each other at slightly different phases — which reads
// as noise rather than as an alarm, and gets worse with every extra PC.
//
// There is now exactly ONE alarm for the whole page. Tiles register while they
// have a repeating notification and unregister when it clears; the alarm runs
// while at least one registration exists, at a single steady cadence, whether
// that is one finished visitor or ten.
// ---------------------------------------------------------------------------

const ALARM_INTERVAL_MS = 10000;

type AlarmPref = { enabled: boolean; volume: number };

const activeAlarms = new Map<string, () => AlarmPref>();
let alarmTimer: ReturnType<typeof setInterval> | null = null;

function ringActiveAlarms() {
  // Resolved fresh every tick, so changing volume or muting mid-alarm takes
  // effect immediately. An enabled alarm is never silenced by a muted one
  // sharing the cadence, and the loudest enabled one sets the level.
  let enabled = false;
  let volume = 0;
  for (const resolve of activeAlarms.values()) {
    const pref = resolve();
    if (pref.enabled) {
      enabled = true;
      volume = Math.max(volume, pref.volume);
    }
  }
  if (enabled) playDangerAlert(volume);
}

/**
 * Register (`resolve` given) or clear (`null`) one tile's repeating alarm.
 *
 * The first registration rings immediately and starts the cadence; later ones
 * join the existing cadence rather than ringing on their own, which is what
 * keeps two simultaneous alerts sounding like one alarm. The cadence stops when
 * the last registration clears.
 */
function setAlarm(key: string, resolve: (() => AlarmPref) | null) {
  if (resolve) {
    const isNew = !activeAlarms.has(key);
    activeAlarms.set(key, resolve);
    if (isNew && alarmTimer === null) {
      ringActiveAlarms();
      alarmTimer = setInterval(ringActiveAlarms, ALARM_INTERVAL_MS);
    }
    return;
  }
  if (activeAlarms.delete(key) && activeAlarms.size === 0 && alarmTimer !== null) {
    clearInterval(alarmTimer);
    alarmTimer = null;
  }
}

// RealityAgent's two end-of-run alerts (see RealityAgent/api/nearlydone.go).
//
// Stage 1, "Bezoeker is bijna klaar", fires for every experience on the final
// scene — the prepare signal. We hook this id to push a "Volgende: …" signal to
// the queue widget + second screen.
const NEARLY_DONE_NOTIFICATION_ID = 'last_level_warning';

// Stage 2, "Bezoeker is nu klaar", fires only when the build reports the visitor
// has actually finished (NEON's highscore entry is over). The agent retires
// stage 1 as it raises this, so the two are never on screen together. Rendered
// louder than a normal banner, and optionally blinks the whole tile.
const VISITOR_DONE_NOTIFICATION_ID = 'visitor_done';

// The agent's two crash-ladder banners (api/crash_recovery.go): the transient
// "restarting" warning and the persistent "keeps crashing" error. Their agent
// text is English and says nothing about which attempt we are on, so the tile
// renders the same Dutch ladder copy the body uses (services/tileRecovery
// crashRecoveryView) in their place, rather than adding a second banner.
const CRASH_NOTIFICATION_IDS = ['crash_recovery', 'crash_recovery_failed'];

// RealityAgent's "experience running but no VR stream" notification id (agent
// const streamIssueNotificationID). We attach an "Attempt auto repair" action to
// this specific banner; the agent runs the classify→fix→verify recover sequence
// and streams progress back over the websocket as `repair_progress`.
const VR_NO_STREAM_NOTIFICATION_ID = 'vr_no_stream';

// Shape of the agent's repair_progress websocket payload (RealityAgent
// api/repair.go repairResult). Held per-tile to drive the repair button UI.
interface RepairStatus {
  category: string; // which incident category is being repaired (matches notification.category)
  phase: string; // started | stop_experience | display_reset | bs_restart | verify | done | blocked
  running: boolean;
  recovered: boolean;
  needsReboot: boolean;
  message: string;
}

// Generate a consistent HSL color from a string (experience name).
// Exported: the Insights RunSceneStrip reuses it so run bands match tile colors.
export function nameToColor(name: string): { border: string; bg: string } {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = ((hash % 360) + 360) % 360;
  return {
    border: `hsl(${hue}, 60%, 50%)`,
    bg: `hsla(${hue}, 60%, 50%, 0.15)`,
  };
}

interface PCTileProps {
  pcInstance: PCInstance;
  experiences: ConfigurationRelease[];
  onRefresh?: () => void;
  isAdmin?: boolean;
  onMultiplayerSelect?: (release: ConfigurationRelease) => void;
  roomId?: string;
  // Reports whether an experience is running on this PC, so room-wide actions
  // (Dagstart) can refuse to reboot a PC out from under a visitor.
  onExperienceStateChange?: (pcId: string, running: boolean) => void;
}

export default function PCTile({ pcInstance, experiences, onRefresh, isAdmin = false, onMultiplayerSelect, roomId, onExperienceStateChange }: PCTileProps) {
  const [agentClient, setAgentClient] = useState<PCAgentClient | null>(null);
  const [isOnline, setIsOnline] = useState(false);
  // Liveness lifecycle: last time /api/status answered, a freshly re-fetched DB
  // row (to read the agent's pre-shutdown intent while unreachable), and a timer
  // that re-renders so the reconnecting→unreachable→off transitions advance.
  // Seeded from the PC's last heartbeat, NOT from mount time. Mounting is not
  // evidence of contact, and this component remounts constantly — the dashboard
  // gates the whole PC grid on isLoading, so every refresh, room switch and view
  // switch rebuilds every tile. Seeding with Date.now() made a freshly-mounted
  // tile claim it had just been reachable, so any PC whose heartbeat was younger
  // than the off-timeout showed "Verbinding maken…" (which hides the wake button)
  // for the full reconnect grace after every single refresh.
  const lastReachableAtRef = useRef<number>(
    pcInstance.last_heartbeat ? new Date(pcInstance.last_heartbeat).getTime() : 0
  );
  const [dbRow, setDbRow] = useState<Partial<PCInstance> | null>(null);
  const [, setOfflineTick] = useState(0);
  const [currentExperience, setCurrentExperience] = useState<any>(null);
  const [experienceStartTime, setExperienceStartTime] = useState<number | null>(null);
  const [clockOffset, setClockOffset] = useState<number>(0); // server clock - client clock (ms)
  const [avgRuntimeSeconds, setAvgRuntimeSeconds] = useState<number | null>(null);
  const [volume, setVolume] = useState<number>(50);
  const [picoStatus, setPicoStatus] = useState<any>(null);
  const [mpConnected, setMpConnected] = useState<boolean | undefined>(undefined);
  const [mpShadowRelayed, setMpShadowRelayed] = useState(false);
  const [mpShadowFloor, setMpShadowFloor] = useState<{ source: 'headset' | 'knob'; m?: number } | null>(null);
  const [hmdBoundary, setHmdBoundary] = useState<HmdBoundaryStatus | null>(null);
  const [mpServerIP, setMpServerIP] = useState<string | undefined>(undefined);
  const [headsetStatus, setHeadsetStatus] = useState<HeadsetStatusType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [wakeRequested, setWakeRequested] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checklistConfirmed, setChecklistConfirmed] = useState(false);
  const [showLanguageSelect, setShowLanguageSelect] = useState(false);
  const [selectedExperienceForLang, setSelectedExperienceForLang] = useState<any>(null);
  const [skipBilling, setSkipBilling] = useState<boolean>(false);
  const [forceUpdate, setForceUpdate] = useState<boolean>(false);
  const [updateAvailable, setUpdateAvailable] = useState<boolean>(false);
  const [dangerZone, setDangerZone] = useState<boolean>(false);
  const [dangerZoneReason, setDangerZoneReason] = useState<string>('');
  const dangerZoneRef = React.useRef<boolean>(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [launchProgress, setLaunchProgress] = useState<number>(0);
  const [isLaunching, setIsLaunching] = useState<boolean>(false);
  const [showScenePicker, setShowScenePicker] = useState(false);
  const [showRedPopup, setShowRedPopup] = useState(false);
  const [showMarkIssue, setShowMarkIssue] = useState(false);
  const [showSendMessage, setShowSendMessage] = useState(false);
  // PICO headset agents seen by this PC's RealityAgent (null = agent too old / none)
  const [hmdList, setHmdList] = useState<HmdListData | null>(null);
  // Serials assigned to this PC in infra.hmd_devices (desired assignment).
  const [hmdAssignedSerials, setHmdAssignedSerials] = useState<string[]>([]);
  const [showHmdSheet, setShowHmdSheet] = useState(false);
  const [hmdBusy, setHmdBusy] = useState(false);
  // Autodiagnostics: agent's /api/status diagnostics block (null = old agent
  // or agent unreachable ⇒ exactly the pre-diagnostics behavior).
  const [diagnostics, setDiagnostics] = useState<DiagnosticsStatus | null>(null);
  const [showDiagOverride, setShowDiagOverride] = useState(false);
  const [showDiagReport, setShowDiagReport] = useState(false);
  // Build-sync availability: agent's /api/status sync block (null = old agent
  // or unreachable ⇒ exactly today's behavior, no sync UI).
  const [syncStatus, setSyncStatus] = useState<SyncStatusBlock | null>(null);
  // Crash recovery: agent's /api/status crash_recovery block (null = old agent
  // or unreachable ⇒ no crash on record). Without it a crash and a visitor who
  // finished are indistinguishable here: the experience just disappears.
  const [crashRecovery, setCrashRecovery] = useState<CrashRecovery | null>(null);
  const [toast, setToast] = useState<ToastData | null>(null);
  const [repairStatus, setRepairStatus] = useState<RepairStatus | null>(null);
  // Auto-repair countdown (reboot-retry): fed by notifications whose
  // meta.kind === 'auto_repair' (poll + socket) and pre-seeded by the
  // repair_progress 'rebooting' frame. Kept across the PC's offline reboot
  // window so the tile can keep counting down while the agent is dead;
  // verdict phases clear it. startedAtMs/estimateS come from the agent
  // (agent clock — compare against adjustedNow()).
  const [autoRepair, setAutoRepair] = useState<{
    phase: string;
    startedAtMs: number;
    estimateS: number;
  } | null>(null);
  const [scenes, setScenes] = useState<{ index: number; name: string }[]>([]);
  const [loadingScenes, setLoadingScenes] = useState(false);
  const [sceneCount, setSceneCount] = useState<number | null>(null);
  // Keys this tile currently holds in the page-wide alarm, so they can be
  // released when the notification clears or the tile unmounts. A leaked key
  // would keep the shared alarm ringing forever.
  const alarmKeysRef = React.useRef<Set<string>>(new Set());
  const lastBeepedNotificationsRef = React.useRef<Set<string>>(new Set());
  // Separate ref from beep tracking — we only want to push the queue's
  // "Volgende: …" signal once per experience-run, not on every status poll.
  const lastPushedNextUpRef = React.useRef<Set<string>>(new Set());
  // Last applied notification list, so a socket event can merge into it without
  // waiting for the next poll (and without re-rendering to read state).
  const notificationsRef = React.useRef<Notification[]>([]);
  const wsRef = React.useRef<WebSocket | null>(null);
  const progressIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const onRefreshRef = React.useRef(onRefresh);
  const queueCtx = useQueueDequeueOptional();
  const queueCtxRef = React.useRef(queueCtx);
  React.useEffect(() => {
    queueCtxRef.current = queueCtx;
  }, [queueCtx]);
  // Queue-first dispatch (null outside queue mode / on QueueDisplay). Held in
  // a ref for the report/registry effects so context identity churn (its
  // pcReports map changes on every PC transition) never re-fires them.
  const dispatchCtx = useQueueDispatchOptional();
  const dispatchCtxRef = React.useRef(dispatchCtx);
  React.useEffect(() => {
    dispatchCtxRef.current = dispatchCtx;
  }, [dispatchCtx]);
  // Latest launch handler for the dispatch registry (defined further down;
  // assigned every render so the registered closure never goes stale).
  const launchForDispatchRef = React.useRef<
    (experienceId: string, version: string, language: string | null, opts?: { dequeueEntryId?: string }) => Promise<boolean>
  >(async () => false);

  // Notification-sound preferences (device-local). Held in a ref so the status
  // poll's beep logic — which runs inside a long-lived closure — always reads
  // the latest values without re-subscribing the poll effect.
  const { settings } = useSettings();
  const settingsRef = React.useRef(settings);
  React.useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  // Keep onRefresh ref up to date to avoid stale closures in WebSocket handlers
  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  // Candidate hosts for the agent client, in priority order: LAN first,
  // Tailscale as fallback. The client tries LAN, fails over to Tailscale, and
  // remembers which one works so off-LAN PCs don't time out on every request.
  // `pcIP` is just the primary, used for display and the !pcIP guard.
  const pcIP = pcInstance.ip_address || pcInstance.tailscale_ip;

  // Display language code as styled badge
  const getLanguageFlag = (shortCode: string): string => {
    return shortCode.toUpperCase();
  };

  // Display player_index for operators, index + pc_name for admins
  const displayName = isAdmin
    ? `${pcInstance.player_index ?? '?'}: ${pcInstance.pc_name || 'Unknown PC'}`
    : (pcInstance.player_index?.toString() || '?');

  // Check operator permissions
  const [operatorPermissions, setOperatorPermissions] = useState<string[]>([]);

  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const operatorData = sessionStorage.getItem('current_operator');
        if (!operatorData) {
          return;
        }
        const operator: Operator = JSON.parse(operatorData);
        const rolePerms = await getRolePermissions(operator.role);

        // Merge per-operator permissions from the JSONB field
        const operatorPerms = operator.permissions
          ? Object.keys(operator.permissions).filter(k => operator.permissions![k] === true)
          : [];

        const merged = Array.from(new Set([...rolePerms, ...operatorPerms]));
        setOperatorPermissions(merged);
      } catch (error) {
        console.error('Error loading operator permissions:', error);
      }
    };
    loadPermissions();
  }, []);

  const canKillExperience = isAdmin || operatorPermissions.includes('kill_experience');
  const canInvalidateRun = operatorPermissions.includes('invalidate_experience_run');
  const canRestartPC = isAdmin || operatorPermissions.includes('restart_pc');
  const canShutdownPC = isAdmin || operatorPermissions.includes('shutdown_pc');
  const canRestartAgent = isAdmin || operatorPermissions.includes('restart_agent');
  const canControlParsec = isAdmin || operatorPermissions.includes('control_parsec');
  const canCaptureIncident = isAdmin || operatorPermissions.includes('capture_incident');
  const canMarkIssue = isAdmin || operatorPermissions.includes('mark_issue');
  const canSendVisitorMessage = isAdmin || operatorPermissions.includes('send_visitor_message');
  const canSkipBilling = isAdmin || operatorPermissions.includes('skip_billing');
  const canControlLevels = isAdmin || operatorPermissions.includes('control_experience_levels');
  const requiresChecklist = operatorPermissions.includes('require_checklist');
  const canViewReleaseDetails = isAdmin || operatorPermissions.includes('view_release_details');
  // Bypass the charged-headphone launch requirement (PICO must still be connected).
  // Admins retain their full no-device bypass separately (see canLaunch below).
  const canLaunchWithoutHeadsetCharge = isAdmin || operatorPermissions.includes('launch_without_charged_headset');
  const showRedButton = canShutdownPC || canRestartPC || canRestartAgent || canControlParsec || canCaptureIncident || canMarkIssue || canSkipBilling || canSendVisitorMessage;

  // Derived headset values
  const headsetIsCharged = headsetStatus?.connected === true && (headsetStatus?.battery_level ?? 0) > 15;
  const headsetBattery = headsetStatus?.connected ? headsetStatus.battery_level : null;

  // This PC's headset: the one Business Streaming reports as paired, else the one
  // assigned to this PC in the database. Never "whichever headset my agent holds a
  // socket for" — the room primary holds sockets for every headset in the room, so
  // that inference claimed other PCs' headsets (and painted them streaming-down).
  const pairedHmdSerial = hmdList?.paired_serial ?? hmdAssignedSerials[0] ?? null;
  const pairedHmd: HmdInfo | null =
    (pairedHmdSerial ? hmdList?.headsets.find((h) => h.serial === pairedHmdSerial) : null) ?? null;
  const hmdAgentOnline = pairedHmd !== null;
  // Red = something an operator should act on: streaming down while the agent can still reach the headset.
  const hmdStreamingDown = hmdAgentOnline && picoStatus?.connected === false;
  const canRestartHmdStreaming = isAdmin || operatorPermissions.includes('control_parsec');
  const canRebootHmd = isAdmin || operatorPermissions.includes('restart_pc');

  // Derived diagnostics values. `can_launch` is THE single field launch gating
  // reads — an absent diagnostics block (old agent) means allowed.
  const diagState = diagnostics?.state;
  const diagCanLaunch = diagnostics?.can_launch !== false;
  const diagPendingHmd = diagnostics?.pending_hmd_tests || [];

  // Derived sync values. Launch gating combines diagCanLaunch && syncCanLaunch;
  // the takeover renders only in block mode (report_only shows a passive chip).
  const syncPaused = syncStatus?.paused?.active === true;
  const syncBusy = syncStatus?.busy === true;
  const syncCanLaunch = syncStatus?.can_launch !== false;
  const syncBlockedTakeover = syncBusy && syncStatus?.gate === 'block' && !syncPaused;

  // "fix_convai_restart" → "convai restart"; "convai_liveness" → "convai liveness"
  const humanizeDiagId = (id: string): string => id.replace(/^fix_/, '').replace(/_/g, ' ');

  // Resolve the device-local sound preference for a notification. The
  // "visitor almost finished" chime (last_level_warning) and the generic
  // "something went wrong" alert (everything else that beeps — error
  // notifications and any agent-flagged beep) have independent enable + volume
  // settings so an operator can silence or quieten one without the other.
  const soundPrefFor = (notification: Notification): { enabled: boolean; volume: number } => {
    const s = settingsRef.current;
    // Both end-of-run stages share the visitor-finishing sound settings. The
    // done alert is typed 'error' for its red styling, but it is not a
    // malfunction, so it must not fall through to the went-wrong alert.
    if (
      notification.id === NEARLY_DONE_NOTIFICATION_ID ||
      notification.id === VISITOR_DONE_NOTIFICATION_ID
    ) {
      return { enabled: s.nearlyDoneSoundEnabled, volume: s.nearlyDoneVolume };
    }
    return { enabled: s.errorSoundEnabled, volume: s.errorVolume };
  };

  // Apply a full notification list: beeps, the "next up" fan-out, and render
  // state. Driven by BOTH the 8s /api/status poll and the agent's live
  // notification_started/notification_stopped socket events, so the two can
  // never disagree about what has already beeped. Idempotent — re-applying the
  // same list is a no-op, which is what makes the poll safe after a socket
  // event has already handled the same notification.
  const applyNotifications = (newNotifications: Notification[]) => {
    notificationsRef.current = newNotifications;
    const currentNotificationIds = new Set(newNotifications.map((n: Notification) => n.id));

    // Check for new notifications that need beeping
    for (const notification of newNotifications) {
      const alreadyBeeped = lastBeepedNotificationsRef.current.has(notification.id);
      const beep = notification.beep || (notification.type === 'error' ? 'once' : 'none');
      // Device-local sound preference for this notification's category.
      // When disabled we still mark it beeped so re-enabling mid-run doesn't
      // suddenly fire for a notification that has been on screen for a while.
      const pref = soundPrefFor(notification);

      if (beep === 'once' && !alreadyBeeped) {
        // Beep once for new notification
        if (pref.enabled) playDangerAlert(pref.volume);
        lastBeepedNotificationsRef.current.add(notification.id);
      }
    }

    // Repeating notifications join the one page-wide alarm rather than each
    // starting their own interval. Registration is keyed per PC + notification
    // and is idempotent, so re-applying the same list keeps the existing cadence
    // instead of restarting it.
    const wantedAlarms = new Set<string>();
    for (const notification of newNotifications) {
      const beep = notification.beep || (notification.type === 'error' ? 'once' : 'none');
      if (beep !== 'repeat') continue;
      const key = `${pcInstance.id}:${notification.id}`;
      wantedAlarms.add(key);
      setAlarm(key, () => soundPrefFor(notification));
    }
    // Mutated in place rather than reassigned: the unmount cleanup holds a
    // reference to this exact Set, and swapping it would leave that cleanup
    // releasing a stale key list.
    const held = alarmKeysRef.current;
    for (const key of held) {
      if (!wantedAlarms.has(key)) setAlarm(key, null);
    }
    held.clear();
    for (const key of wantedAlarms) held.add(key);

    // Clean up beeped tracking for removed notifications
    for (const beepedId of lastBeepedNotificationsRef.current) {
      if (!currentNotificationIds.has(beepedId)) {
        lastBeepedNotificationsRef.current.delete(beepedId);
      }
    }

    // "Bezoeker is bijna klaar" → push the head-of-queue customer's last
    // name to the dashboard banner + second screen so the operator can
    // call the next group forward. De-duped per experience run via
    // lastPushedNextUpRef (cleared on a successful launch and when the
    // notification disappears from the agent's payload).
    const nearlyDone = newNotifications.find(
      (n: Notification) => n.id === NEARLY_DONE_NOTIFICATION_ID,
    );
    if (nearlyDone && !lastPushedNextUpRef.current.has(nearlyDone.id)) {
      lastPushedNextUpRef.current.add(nearlyDone.id);
      const head = queueCtxRef.current?.headEntry ?? null;
      if (head) {
        const signal: NextUpSignal = {
          displayName: queueDisplayName(head),
          pcName: pcInstance.pc_name || `PC ${pcInstance.player_index ?? '?'}`,
        };
        // One call fans out to: dashboard banner (CustomEvent), other
        // browser tabs (BroadcastChannel), Electron secondary window (IPC).
        broadcastNextUp(signal);
      }
    }
    if (!nearlyDone) {
      lastPushedNextUpRef.current.delete(NEARLY_DONE_NOTIFICATION_ID);
    }

    setNotifications(newNotifications);
  };

  // The socket effect below has its own dependency list and would otherwise
  // capture a stale applyNotifications; mirror the existing onRefreshRef pattern.
  const applyNotificationsRef = React.useRef(applyNotifications);
  applyNotificationsRef.current = applyNotifications;

  // Initialize agent client with both candidate hosts (LAN first, Tailscale
  // fallback). Recreate only if either IP actually changes.
  useEffect(() => {
    const hosts = [pcInstance.ip_address, pcInstance.tailscale_ip].filter(Boolean) as string[];
    if (hosts.length > 0) {
      // Pass pc id as cacheKey so the last-good host is remembered across
      // refreshes — a remote operator's cold client then starts on Tailscale
      // instead of paying the LAN probe on every reload.
      const client = createPCAgentClient(hosts, 9090, pcInstance.id);
      setAgentClient(client);
    }
  }, [pcInstance.ip_address, pcInstance.tailscale_ip]);

  // Update elapsed time display every second
  const [, setTimerTick] = useState(0);

  useEffect(() => {
    if (!currentExperience || !experienceStartTime) return;

    const interval = setInterval(() => {
      // Force re-render to update elapsed time
      setTimerTick((tick) => tick + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [currentExperience, experienceStartTime]);

  // Tick every second while a diagnostics run is active so its elapsed-time
  // display updates (same trick as the experience timer above).
  useEffect(() => {
    if (diagState !== 'testing' && diagState !== 'fixing') return;

    const interval = setInterval(() => {
      setTimerTick((tick) => tick + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [diagState]);

  // Tick every second while an auto-repair countdown is live — also while the
  // tile is offline mid-reboot (the offline tick alone is 10s, too coarse for
  // a countdown). Self-clears when a verdict never arrives (agent died
  // mid-repair): 10 min past budget the countdown stops claiming anything and
  // the normal offline/online presentation takes over.
  useEffect(() => {
    if (!autoRepair) return;

    const interval = setInterval(() => {
      const overrunS = (Date.now() - autoRepair.startedAtMs) / 1000 - autoRepair.estimateS;
      if (overrunS > 10 * 60) {
        setAutoRepair(null);
        return;
      }
      setTimerTick((tick) => tick + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [autoRepair]);

  // Derive the auto-repair countdown from the notification stream: the agent
  // stamps meta {kind:'auto_repair', phase, started_at, estimate_s} on every
  // phase of the reboot-retry flow. rebooting/testing start or update the
  // countdown; any verdict phase ends it (the verdict banner then renders as a
  // normal notification). The notification disappearing does NOT clear it —
  // mid-reboot the agent is dead and delivers nothing, which is exactly when
  // the persisted countdown matters.
  useEffect(() => {
    const n = notifications.find((x) => x.meta?.kind === 'auto_repair');
    if (!n?.meta) return;
    const phase = String(n.meta.phase ?? '');
    if (phase === 'rebooting' || phase === 'testing') {
      const startedAtMs = n.meta.started_at ? Date.parse(String(n.meta.started_at)) : NaN;
      const estimateS = Number(n.meta.estimate_s ?? 0);
      if (Number.isFinite(startedAtMs) && estimateS > 0) {
        setAutoRepair({ phase, startedAtMs, estimateS });
      }
    } else if (phase) {
      setAutoRepair(null); // verdict reached
    }
  }, [notifications]);

  // Cleanup intervals on unmount
  useEffect(() => {
    const alarmKeys = alarmKeysRef.current;
    return () => {
      // Hand back every alarm this tile held — a tile removed while alerting
      // (room switch, filter change) must not leave the page-wide alarm ringing.
      for (const key of alarmKeys) setAlarm(key, null);
      alarmKeys.clear();
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);

  // Fetch average runtime when experience starts
  useEffect(() => {
    if (!currentExperience?.id) {
      setAvgRuntimeSeconds(null);
      return;
    }

    const fetchAvgRuntime = async () => {
      try {
        const avgRuntime = await databaseService.getAvgRuntimeSeconds(currentExperience.id);
        setAvgRuntimeSeconds(avgRuntime);
      } catch (err) {
        console.error('Error fetching average runtime:', err);
        setAvgRuntimeSeconds(null);
      }
    };

    fetchAvgRuntime();
  }, [currentExperience?.id]);

  // Fetch scene count when experience starts
  useEffect(() => {
    if (!currentExperience?.id) {
      setSceneCount(null);
      return;
    }

    const fetchSceneCount = async () => {
      try {
        const { count, error } = await supabase
          .schema('experiences')
          .from('experience_scenes')
          .select('*', { count: 'exact', head: true })
          .eq('experience_id', currentExperience.id);
        if (!error && count !== null) setSceneCount(count);
      } catch (err) {
        console.error('Error fetching scene count:', err);
      }
    };

    fetchSceneCount();
  }, [currentExperience?.id]);

  // Push "an experience is running here" up to the dashboard whenever the status
  // poll flips it. Held in a ref so a parent that passes an inline callback
  // doesn't re-fire this on every render.
  const onExperienceStateChangeRef = React.useRef(onExperienceStateChange);
  useEffect(() => {
    onExperienceStateChangeRef.current = onExperienceStateChange;
  }, [onExperienceStateChange]);

  useEffect(() => {
    // A crash relaunch in flight still counts as busy: the process is gone for
    // a few seconds but the visitor is mid-session (keeps Dagstart / day close
    // blocked, like a normal run).
    onExperienceStateChangeRef.current?.(
      pcInstance.id,
      !!currentExperience || !!crashRecovery?.restarting,
    );
  }, [currentExperience, crashRecovery?.restarting, pcInstance.id]);

  // Retract the report when the tile goes away (room switch, view change), so a
  // stale "busy" never keeps a room-wide action blocked forever.
  useEffect(() => {
    const pcId = pcInstance.id;
    return () => {
      onExperienceStateChangeRef.current?.(pcId, false);
    };
  }, [pcInstance.id]);

  // --- Customer-queue recovery (queue mode only) ----------------------------
  // The visitor playing on this PC, from fn_get_started_today (started_pc_id).
  // Guarded against a stale row from an EARLIER run on this PC: the row's last
  // event (start or move) must not predate the running experience by more
  // than 5 minutes when the tile knows the start time.
  // A crash counts as "still playing here": between the crash and the relaunch
  // the agent reports no experience at all, but the visitor never left the PC
  // and the row is still 'started'. Declared before the dispatch report, which
  // reads it (a crash the agent gave up on must not free the PC).
  const crashView = crashRecoveryView(crashRecovery);
  const queueVisitor: StartedEntry | null = React.useMemo(() => {
    if (!currentExperience && !crashRecovery?.restarting && !crashRecovery?.gave_up) return null;
    const s = queueCtx?.startedByPc.get(pcInstance.id) ?? null;
    if (!s) return null;
    if (experienceStartTime) {
      const eventAt = Date.parse(s.last_event_at || s.started_at);
      if (Number.isFinite(eventAt) && eventAt < experienceStartTime - 5 * 60_000) return null;
    }
    return s;
  }, [currentExperience, crashRecovery, queueCtx, pcInstance.id, experienceStartTime]);

  // Queue-first dispatch: report this PC's dispatchability to the room-level
  // provider on every transition (fed by the same 8s poll — no extra traffic),
  // and register the imperative launch used by the dispatch pop-up. Both are
  // no-ops outside queue mode (dispatchCtx null).
  //
  // Crash ladder (see services/tileRecovery crashRecoveryView):
  //  - relaunch in flight  -> running, recovering: the same visitor is coming
  //    back on this PC, so the tile must not read as "the visitor finished";
  //  - agent gave up WITH a queue visitor -> free but recovering, so the PC is
  //    not auto-dispatched while that visitor is still unresolved;
  //  - gave up without a queue visitor (walk-in, queue off) -> nothing to
  //    resolve in the queue, so exactly today's behaviour.
  const crashRestarting = !!crashRecovery?.restarting;
  const dispatchRunning = !!currentExperience || isLaunching || crashRestarting;
  const dispatchRecovering = crashRestarting || (!!crashRecovery?.gave_up && !!queueVisitor);
  const dispatchCanLaunch =
    (isAdmin || ((picoStatus?.connected || false) && (headsetIsCharged || canLaunchWithoutHeadsetCharge))) &&
    diagCanLaunch && syncCanLaunch;
  useEffect(() => {
    dispatchCtxRef.current?.reportPC(pcInstance.id, {
      pcName: pcInstance.pc_name ?? pcInstance.id,
      online: isOnline,
      running: dispatchRunning,
      canLaunch: dispatchCanLaunch,
      recovering: dispatchRecovering,
    });
  }, [pcInstance.id, pcInstance.pc_name, isOnline, dispatchRunning, dispatchCanLaunch, dispatchRecovering]);
  useEffect(() => {
    // Mount-scoped on purpose: the provider (when present) wraps the tiles, so
    // it exists by the time this runs; depending on the context VALUE would
    // re-fire on every pcReports change and the cleanup would wrongly retract
    // this PC's report between transitions.
    const pcId = pcInstance.id;
    dispatchCtxRef.current?.registerLaunch(pcId, (release, language, dequeueEntryId) =>
      launchForDispatchRef.current(
        release.experience_id,
        release.experience_release?.version || '1.0.0',
        language,
        { dequeueEntryId },
      ),
    );
    return () => {
      dispatchCtxRef.current?.retractPC(pcId);
      dispatchCtxRef.current?.unregisterLaunch(pcId);
    };
  }, [pcInstance.id]);

  // Poll PC status
  useEffect(() => {
    if (!agentClient) return;

    const fetchStatus = async () => {
      try {
        const status = await agentClient.getStatus();
        if (status) {
          // Success! Agent is responding
      setIsOnline(true);
      lastReachableAtRef.current = Date.now();
      const newExperience = status.current_experience || null;

      // Calculate clock offset from status poll (server_time - client_time)
      if (status.server_time) {
        setClockOffset(new Date(status.server_time).getTime() - Date.now());
      }

      // Use server's start_time instead of client-side tracking
      if (newExperience?.start_time) {
        const startTimeMs = new Date(newExperience.start_time).getTime();
        setExperienceStartTime(startTimeMs);
      } else {
        setExperienceStartTime(null);
      }

      setCurrentExperience(newExperience);
      setPicoStatus(status.pico || null);
      setMpConnected(status.mp_connected);
      setMpServerIP(status.mp_server_ip);
      setMpShadowRelayed(!!status.mp_shadow?.relayed);
      setMpShadowFloor(status.mp_shadow ? { source: status.mp_shadow.floor_source === 'headset' ? 'headset' : 'knob', m: status.mp_shadow.floor_m } : null);
      setHmdBoundary(status.hmd_boundary ?? null);
      // The 8s poll is authoritative for diagnostics; the WS
      // diagnostics_changed event only sugars latency between polls.
      setDiagnostics(status.diagnostics || null);
      // Same contract for the build-sync block (absent = old agent).
      setSyncStatus(status.sync || null);
      // Same contract for crash recovery: absent = old agent = no crash.
      setCrashRecovery(status.crash_recovery ?? null);

      // Handle loading state from server (fallback for WebSocket)
      if (newExperience?.is_loading) {
        // Experience is loading - show loading bar if not already showing
        if (!isLaunching) {
          setIsLaunching(true);
          setLaunchProgress(50); // Show indeterminate progress
        }
      } else if (isLaunching && newExperience && !newExperience.is_loading) {
        // Experience finished loading - stop loading bar (fallback for WebSocket)
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        setLaunchProgress(100);
        setTimeout(() => {
          setIsLaunching(false);
          setLaunchProgress(0);
          // Now it's safe to call onRefresh since loading state is complete
          if (onRefreshRef.current) onRefreshRef.current();
        }, 500);
      }

      // Handle notifications
      applyNotificationsRef.current(status.notifications || []);

      // Handle danger zone status changes (backwards compatibility)
      const newDangerZone = status.danger_zone || false;
      const newDangerZoneReason = status.danger_zone_reason || '';

      dangerZoneRef.current = newDangerZone;
      setDangerZone(newDangerZone);
      setDangerZoneReason(newDangerZoneReason);
      setError(null);
        } else {
          // No response from agent
          setIsOnline(false);

      setCurrentExperience(null);
      setExperienceStartTime(null);
      setPicoStatus(null);
      setMpConnected(undefined);
      setMpServerIP(undefined);
      setDiagnostics(null);
      setSyncStatus(null);
      setCrashRecovery(null);
        }
      } catch (err) {
        // Failed to reach agent
        setIsOnline(false);

        setCurrentExperience(null);
        setExperienceStartTime(null);
        setPicoStatus(null);
        setMpConnected(undefined);
        setMpServerIP(undefined);
        setDiagnostics(null);
        setSyncStatus(null);
        setCrashRecovery(null);
      }
    };

    const fetchVolume = async () => {
      const vol = await agentClient.getVolume();
      if (vol !== null) {
        setVolume(vol);
      }
    };

    const fetchHeadsetStatus = async () => {
      try {
        const headset = await agentClient.getHeadsetStatus();
        setHeadsetStatus(headset);
      } catch (err) {
        setHeadsetStatus(null);
      }
    };

    const fetchHmd = async () => {
      setHmdList(await agentClient.getHmdList());
      const rows = await databaseService.getHmdDevicesForPC(pcInstance.id);
      setHmdAssignedSerials(rows.map((r) => (r.serial || '').toUpperCase()).filter(Boolean));
    };

    const fetchUpdateStatus = async () => {
      try {
        const result = await agentClient.checkUpdate();
        if (result.success && result.data) {
          setUpdateAvailable(result.data.update_available);
        }
      } catch (err) {
        console.error('Error checking for updates:', err);
      }
    };

    fetchStatus();
    fetchVolume();
    fetchHeadsetStatus();
    fetchHmd();
    fetchUpdateStatus();

    const statusInterval = setInterval(fetchStatus, 8000); // Poll every 8 seconds
    const volumeInterval = setInterval(fetchVolume, 10000); // Poll volume every 10 seconds
    const headsetInterval = setInterval(fetchHeadsetStatus, 10000); // Poll headset every 10 seconds
    const hmdInterval = setInterval(fetchHmd, 10000); // Poll headset agents every 10 seconds
    const updateInterval = setInterval(fetchUpdateStatus, 60000); // Check for updates every 60 seconds

    return () => {
      clearInterval(statusInterval);
      clearInterval(volumeInterval);
      clearInterval(headsetInterval);
      clearInterval(hmdInterval);
      clearInterval(updateInterval);
    };
  }, [agentClient]);

  // While the agent is unreachable, re-fetch this PC's DB row so we can read the
  // lifecycle intent it wrote right before going down (restarting/updating/
  // shutting_down) and the diagnostics reboot mirror. The /api/status poll is
  // dead then, and the pcInstance prop only refreshes on a manual room refresh —
  // without this, an intent the agent wrote seconds before dying is never seen.
  useEffect(() => {
    if (isOnline) {
      setDbRow(null);
      return;
    }
    let cancelled = false;
    const fetchRow = async () => {
      try {
        const row = await databaseService.getPCInstanceById(pcInstance.id);
        if (!cancelled && row) setDbRow(row);
      } catch {
        /* keep last known row */
      }
    };
    fetchRow();
    const iv = setInterval(fetchRow, 12000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [isOnline, pcInstance.id]);

  // Re-render on a timer while offline so the reconnecting→unreachable→off
  // transitions advance — repeated failing polls call setIsOnline(false) with an
  // unchanged value, which React bails on, so nothing else drives the clock.
  useEffect(() => {
    if (isOnline) return;
    const iv = setInterval(() => setOfflineTick((t) => t + 1), 10000);
    return () => clearInterval(iv);
  }, [isOnline]);

  // WebSocket connection for real-time updates. Connects to whichever host the
  // agent client currently treats as active (LAN or Tailscale), so it follows
  // the same failover the HTTP polling drives. Reconnects on drop and re-reads
  // the active host each time, so a mid-session failover is picked up.
  useEffect(() => {
    if (!agentClient && !pcIP) return;

    let cancelled = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let hostWatch: ReturnType<typeof setInterval> | null = null;
    // Host the live socket was opened with — so the watchdog below can notice a
    // mid-session HTTP failover (LAN -> Tailscale) and re-point the socket
    // instead of hanging on the dead host until the OS TCP timeout.
    let connectedHost: string | null = null;

    const connectWebSocket = () => {
      if (cancelled) return;
      const host = agentClient?.getActiveHost() || pcIP;
      if (!host) return;
      connectedHost = host;
      const ws = new WebSocket(`ws://${host}:9090/ws`);

      ws.onopen = () => {
        console.log(`WebSocket connected to ${host}`);
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          if (message.type === 'experience_loaded') {
            // Calculate clock offset between server and client (server - client)
            if (message.timestamp) {
              const serverTime = new Date(message.timestamp).getTime();
              setClockOffset(serverTime - Date.now());
            }
            // Experience finished loading - stop the loading bar
            if (progressIntervalRef.current) {
              clearInterval(progressIntervalRef.current);
              progressIntervalRef.current = null;
            }
            setLaunchProgress(100);
            setTimeout(() => {
              setIsLaunching(false);
              setLaunchProgress(0);
              // Now it's safe to call onRefresh since loading state is complete
              if (onRefreshRef.current) onRefreshRef.current();
            }, 500);
          } else if (message.type === 'diagnostics_changed') {
            // Latency sugar only — merge into the last polled diagnostics
            // block; the 8s /api/status poll stays authoritative. Never
            // synthesize a diagnostics block from the event alone (an event
            // arriving before the first poll would be missing most fields).
            const d = message.data || {};
            setDiagnostics((prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                state: d.state ?? prev.state,
                can_launch: typeof d.can_launch === 'boolean' ? d.can_launch : prev.can_launch,
                progress: d.progress
                  ? {
                      done: d.progress.done,
                      total: d.progress.total,
                      current_test: d.current_test ?? prev.progress?.current_test ?? '',
                    }
                  : prev.progress,
              };
            });
          } else if ((message.type === 'notification_started' || message.type === 'notification_shown') && message.data) {
            // Live notification push. Without this the tile only learned about
            // notifications on the 8s status poll, which delayed the "Bezoeker
            // is bijna klaar" chime — and therefore the call-forward of the next
            // group — by up to a full poll interval. The poll stays
            // authoritative for everything else; this only front-runs it.
            // The agent emits BOTH type strings ('notification_shown' from most
            // banners, 'notification_started' from nearly-done) — accept both,
            // otherwise incident/repair banners wait for the poll.
            const d = message.data;
            const incoming: Notification = {
              id: String(d.id),
              type: d.type,
              text: String(d.text ?? ''),
              // The socket frame carries no start_time; the next poll corrects it.
              start_time: new Date().toISOString(),
              duration: d.duration ?? undefined,
              beep: d.beep ?? undefined,
              category: d.category ?? undefined,
              actions: d.actions ?? undefined,
              confirm_dismiss: d.confirm_dismiss ?? undefined,
              confirm_reboot: d.confirm_reboot ?? undefined,
              meta: d.meta ?? undefined,
            };
            const rest = notificationsRef.current.filter((n) => n.id !== incoming.id);
            applyNotificationsRef.current([...rest, incoming]);
          } else if (message.type === 'notification_stopped' && message.data) {
            const id = String(message.data.id);
            applyNotificationsRef.current(
              notificationsRef.current.filter((n) => n.id !== id),
            );
          } else if (message.type === 'sync_changed' && message.data) {
            // The agent sends the FULL sync block on every transition (pull
            // start/stop, serve start/stop, pause/resume, defer), so replace
            // wholesale; agents without sync control never emit this event.
            setSyncStatus(message.data as SyncStatusBlock);
          } else if (message.type === 'repair_progress' && message.data) {
            // No-VR auto-repair progress: { phase, result: repairResult }. Drive
            // the "Attempt auto repair" button; on needs_reboot the banner offers
            // a reboot. 'done'/'blocked' end the run.
            const r = message.data.result || {};
            const phase = String(message.data.phase || '');
            setRepairStatus({
              category: String(r.category || 'experience_no_vr'),
              phase,
              running: phase !== 'done' && phase !== 'blocked',
              recovered: !!r.recovered,
              needsReboot: !!r.needs_reboot,
              message: String(r.message || ''),
            });
            // Reboot-retry: seed the countdown immediately from the broadcast —
            // the notification (with the authoritative started_at) corrects it
            // moments later, but the PC may go dark before the next poll.
            if (phase === 'rebooting' && Number(r.estimate_seconds) > 0) {
              setAutoRepair({
                phase: 'rebooting',
                startedAtMs: Date.now(),
                estimateS: Number(r.estimate_seconds),
              });
            }
            // 'blocked' means the sequence never ran (e.g. it could not stop the
            // experience). That verdict only arrives here — the POST returns 202
            // before the outcome is known — so surface it, or it looks like the
            // repair silently succeeded.
            if (phase === 'blocked') {
              setToast({ kind: 'error', message: `Auto-herstel geblokkeerd: ${String(r.message || 'onbekende reden')}` });
            }
          }
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };

      ws.onclose = () => {
        if (cancelled) return;
        console.log(`WebSocket disconnected from ${host}, reconnecting...`);
        // Reconnect after 3 seconds (re-reads the active host on next attempt)
        reconnectTimer = setTimeout(connectWebSocket, 3000);
      };

      ws.onerror = (err) => {
        console.error(`WebSocket error for ${host}:`, err);
        ws.close();
      };

      wsRef.current = ws;
    };

    connectWebSocket();

    // Watchdog: if HTTP polling has failed over to a different host than the one
    // the socket is bound to, drop the socket so it reconnects to the now-active
    // host (onclose schedules the reconnect, which re-reads getActiveHost()).
    hostWatch = setInterval(() => {
      if (cancelled) return;
      const active = agentClient?.getActiveHost();
      if (active && connectedHost && active !== connectedHost && wsRef.current) {
        wsRef.current.close();
      }
    }, 5000);

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (hostWatch) clearInterval(hostWatch);
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [agentClient, pcIP]);

  // Handle clicking on an experience - check multiplayer, then language support
  const handleExperienceClick = (release: any) => {
    // Intercept multiplayer or voice-chat experiences — open modal instead of launching directly
    if ((release.experiences?.is_multiplayer || release.experiences?.use_vc) && onMultiplayerSelect) {
      onMultiplayerSelect(release);
      return;
    }

    const supportedLanguages = release.experiences?.supported_languages || [];

    if (supportedLanguages.length === 0) {
      // No language support defined, launch directly
      handleLaunchExperience(release.experience_id, release.experience_release?.version || '1.0.0', null);
    } else if (supportedLanguages.length === 1) {
      // Single language, launch with that language
      handleLaunchExperience(
        release.experience_id,
        release.experience_release?.version || '1.0.0',
        supportedLanguages[0].language?.short_name || null
      );
    } else {
      // Multiple languages: open the language picker modal.
      setSelectedExperienceForLang(release);
      setShowLanguageSelect(true);
    }
  };

  // Launch experience with optional language. Returns true on launch success —
  // the dispatch pop-up (QueueDispatchContext registry) awaits this. `opts.
  // dequeueEntryId` pins the post-success queue pop to a specific person
  // (dispatch flow); without it the widget's dequeue-on-launch behavior applies.
  const handleLaunchExperience = async (
    experienceId: string,
    version: string,
    language: string | null,
    opts?: { dequeueEntryId?: string },
  ): Promise<boolean> => {
    if (!agentClient) return false;

    // Diagnostics gate: the agent's can_launch is the single source of truth
    // (absent block ⇒ old agent ⇒ allowed). The agent also enforces this
    // server-side (409 diagnostics_blocked), handled below as the race case.
    if (diagnostics?.can_launch === false) {
      setToast({ kind: 'error', message: 'Launch blocked — PC checks have not passed' });
      return false;
    }

    setIsLoading(true);
    setIsLaunching(true);
    setLaunchProgress(0);
    setError(null);
    setShowLanguageSelect(false);

    // Clear any existing progress interval
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }

    // Get average loading time from the release, default to 30 seconds if not available
    const release = experiences.find(e => e.experience_id === experienceId && e.experience_release?.version === version);
    const avgLoadingTimeMs = release?.experience_release?.avg_loading_time_ms || 30000;
    const updateInterval = 100; // Update every 100ms
    const totalUpdates = avgLoadingTimeMs / updateInterval;
    const progressIncrement = 95 / totalUpdates; // Reach 95% over the expected loading time

    // Progress bar based on average loading time
    progressIntervalRef.current = setInterval(() => {
      setLaunchProgress(prev => {
        if (prev >= 95) return 95; // Cap at 95% until experience_loaded event
        return Math.min(prev + progressIncrement, 95);
      });
    }, updateInterval);

    try {
      // Get operator ID from session storage
      const operatorData = sessionStorage.getItem('current_operator');
      const operatorId = operatorData ? JSON.parse(operatorData).id : undefined;

      const result = await agentClient.launchExperience({
        experience_id: experienceId,
        version,
        operator_id: operatorId,
        language: language || undefined,
        skip_billing: skipBilling,
      });

      if (result.success) {
        // Reset skip billing after successful launch
        setSkipBilling(false);
        // Reset the next-up de-dupe so the upcoming experience's first
        // last_level_warning gets pushed exactly once.
        lastPushedNextUpRef.current.clear();
        // Pop the customer queue (pessimistic: only on launch success). The
        // dispatch pop-up pins the exact person via opts.dequeueEntryId;
        // otherwise the widget's dequeue-on-launch toggle + selection apply.
        // If another operator already dequeued the same person (race), treat
        // as silent success. Operates on the individual queue_entry_id —
        // a launch pops one person, not the whole party.
        const dequeueTargetId =
          opts?.dequeueEntryId ??
          (queueCtx?.dequeueOnLaunch ? queueCtx.selectedEntry?.queue_entry_id : undefined);
        if (dequeueTargetId && operatorId) {
          try {
            await dequeueForLaunch(dequeueTargetId, operatorId, pcInstance.id);
          } catch (dequeueErr) {
            if (!isNotQueuedError(dequeueErr)) {
              console.error('queue dequeue after launch failed', dequeueErr);
              setError('Launch gelukt, maar wachtrij niet bijgewerkt — controleer handmatig.');
            }
          }
          // Reset the manual override so the next launch follows the
          // (now-new) head of queue unless the operator re-picks.
          queueCtx?.setSelected(null);
        }
        // Keep loading bar visible - WebSocket experience_loaded event will hide it
        // Note: Don't call onRefresh() here - it causes component remount and loses loading state
        // The status will be updated via polling, and onRefresh is called when loading completes
        return true;
      } else {
        // Launch failed - stop progress and show error
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        if (result.error === 'diagnostics_blocked') {
          // Race case: the poll said launchable but the agent 409'd (e.g. a
          // run started in between). The next poll re-syncs the tile.
          setToast({ kind: 'error', message: 'Launch blocked — PC checks are running or failed' });
        } else if (result.error === 'sync_in_progress') {
          // Race case: another operator resumed sync between our pause poke
          // and the launch call. The next poll re-syncs the tile.
          setToast({ kind: 'error', message: 'Launch geblokkeerd — PC synct content. Gebruik "Pauzeer sync & start".' });
        } else {
          setError(result.error || 'Failed to launch experience');
        }
        setIsLaunching(false);
        setLaunchProgress(0);
        return false;
      }
    } catch (err: any) {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      setError(err.message || 'Failed to launch experience');
      setIsLaunching(false);
      setLaunchProgress(0);
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  // Keep the dispatch registry's closure pointing at the freshest handler
  // (agentClient, diagnostics, queueCtx are all captured per render).
  launchForDispatchRef.current = handleLaunchExperience;

  // Confirm checklist
  const handleConfirmChecklist = () => {
    setChecklistConfirmed(true);

    // If only one experience, auto-launch it (with language check)
    if (experiences.length === 1) {
      const release = experiences[0];
      handleExperienceClick(release);
    }
  };

  // Cancel and return to checklist
  const handleCancelLaunch = () => {
    setChecklistConfirmed(false);
  };

  // Kill experience
  const handleKillExperience = async () => {
    if (!agentClient) return;

    const confirmed = window.confirm(
      `Are you sure you want to stop "${currentExperience?.name || 'this experience'}"?`
    );

    if (!confirmed) return;

    setIsLoading(true);
    setError(null);

    try {
      // Get operator ID from session storage
      const operatorData = sessionStorage.getItem('current_operator');
      const operatorId = operatorData ? JSON.parse(operatorData).id : undefined;

      const result = await agentClient.killExperience(false, operatorId);

      if (result.success) {
        setCurrentExperience(null);
        setExperienceStartTime(null);
        setChecklistConfirmed(false);
        if (onRefresh) onRefresh();
      } else {
        setError(result.error || 'Failed to stop experience');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to stop experience');
    } finally {
      setIsLoading(false);
    }
  };

  // Stop without the confirm prompt (the recovery buttons are explicit enough).
  // The helper suppresses the dispatch pop-up's auto-open for the freed PC.
  const silentKill = async (): Promise<boolean> => {
    if (!agentClient) return false;
    const operatorData = sessionStorage.getItem('current_operator');
    const operatorId = operatorData ? JSON.parse(operatorData).id : undefined;
    const result = await agentClient.killExperience(false, operatorId);
    if (!result.success) {
      setError(result.error || 'Stoppen mislukt');
      return false;
    }
    setCurrentExperience(null);
    setExperienceStartTime(null);
    setChecklistConfirmed(false);
    return true;
  };

  // Restart / move / requeue semantics are shared with the staging FakePCTile
  // (services/tileRecovery.ts); this tile only supplies stop + relaunch.
  const recoveryDeps = (): TileRecoveryDeps => ({
    pcId: pcInstance.id,
    pcName: pcInstance.pc_name || `PC ${pcInstance.player_index ?? '?'}`,
    experiences,
    dispatchCtx,
    queueCtx,
    stop: silentKill,
    relaunch: (experienceId, version, language, dequeueEntryId) =>
      handleLaunchExperience(experienceId, version, language, { dequeueEntryId }),
    setBusy: setIsLoading,
    setError,
    // Give the agent a moment to reap the process before relaunching.
    relaunchDelayMs: 2000,
  });

  // What the recovery actions relaunch: whatever runs now, else the run that
  // crashed. After the agent gives up there is no process left, but
  // crash_recovery still names the experience — without this fallback
  // "Opnieuw starten" would be a no-op on exactly the tile that needs it.
  const recoveryCurrent: { id: string; version: string } | null = currentExperience?.id
    ? {
        id: currentExperience.id as string,
        version: (currentExperience.version as string | undefined) || '1.0.0',
      }
    : crashRecovery?.experience_id
      ? {
          id: crashRecovery.experience_id,
          version:
            experiences.find((e) => e.experience_id === crashRecovery.experience_id)
              ?.experience_release?.version || '1.0.0',
        }
      : null;

  // "Opnieuw starten": broken start -> same experience, same language, same
  // queue row (fn_dequeue_entry on a started row = no-op move to this PC).
  const handleQueueRestart = async () => {
    if (!queueVisitor || !recoveryCurrent) return;
    await queueRestart(recoveryDeps(), queueVisitor, recoveryCurrent);
  };

  // "Verplaats naar andere PC": stop here, then the dispatch pop-up opens
  // pinned to this visitor; the other tile's launch moves started_pc_id.
  const handleQueueMove = async () => {
    if (!queueVisitor || !dispatchCtx) return;
    await queueMove(recoveryDeps(), queueVisitor, recoveryCurrent);
  };

  // "Terug in wachtrij": stop here, row back to queued at its original spot.
  const handleQueueRequeue = async () => {
    if (!queueVisitor) return;
    await queueRequeue(recoveryDeps(), queueVisitor);
  };

  // `actions: false` keeps the visitor line but hides the recovery buttons —
  // used while the agent is relaunching after a crash, where the operator has
  // nothing to decide yet.
  const renderQueueVisitor = (opts?: { actions?: boolean }) => {
    if (!queueVisitor) return null;
    const marker = partyMarker(queueVisitor);
    return (
      <div className="tile-queue-visitor">
        <div className="tile-queue-visitor-line" title="Bezoeker uit de wachtrij die op deze PC speelt">
          <LanguageFlag code={queueVisitor.play_language} className="tile-queue-flag" />
          <span className="tile-queue-name">{fullName(queueVisitor)}</span>
          {marker ? <span className="tile-queue-party">{marker}</span> : null}
        </div>
        {canKillExperience && opts?.actions !== false && (
          <div className="tile-queue-actions">
            <button
              className="tile-queue-btn"
              onClick={() => void handleQueueRestart()}
              disabled={isLoading || !recoveryCurrent}
              title="Stop en start dezelfde ervaring opnieuw voor deze bezoeker (zelfde taal, zelfde plek in de administratie)."
            >
              Opnieuw starten
            </button>
            <button
              className="tile-queue-btn"
              onClick={() => void handleQueueMove()}
              disabled={isLoading || !dispatchCtx}
              title="Stop hier en kies een andere PC voor deze bezoeker."
            >
              Verplaats naar andere PC
            </button>
            <button
              className="tile-queue-btn"
              onClick={() => void handleQueueRequeue()}
              disabled={isLoading}
              title="Stop en zet de bezoeker terug in de wachtrij op de oorspronkelijke plek."
            >
              Terug in wachtrij
            </button>
          </div>
        )}
      </div>
    );
  };

  // Crash ladder body — replaces the launch grid ONLY while the situation is
  // abnormal (show by exception), so the operator can never start the next
  // visitor on top of someone whose experience just died:
  //  - amber while the agent relaunches the same run: calm, no buttons, the
  //    visitor line stays so it is obvious who is still sitting here;
  //  - red once the agent gave up: the three recovery actions decide what
  //    happens to that visitor.
  const renderCrashRecoveryBody = () => {
    if (!crashView) return null;
    const gaveUp = crashView.tone === 'gave_up';
    return (
      <div className={`crash-recovery-body${gaveUp ? ' crash-recovery-gaveup' : ''}`}>
        <div className="crash-recovery-headline">
          <span className="crash-recovery-icon" aria-hidden="true">{gaveUp ? '⛔' : '↻'}</span>
          <span>{crashView.text}</span>
        </div>
        <div className="crash-recovery-sub">
          {gaveUp
            ? 'De PC blijft bezet tot je dit voor deze bezoeker oplost.'
            : 'De bezoeker blijft op deze PC; de ervaring komt terug op dezelfde plek.'}
        </div>
        {crashRecovery?.experience_name ? (
          <div className="experience-name-v2">{crashRecovery.experience_name}</div>
        ) : null}
        {renderQueueVisitor({ actions: gaveUp })}
      </div>
    );
  };

  // Invalidate experience run (admin only - deletes run from database)
  const handleInvalidateRun = async () => {
    if (!agentClient) return;

    const confirmed = window.confirm(
      `Are you sure you want to INVALIDATE this experience run?\n\nThis will DELETE the run record from the database and cannot be undone.\n\nUse this only for test runs or mistakes that should not be billed.`
    );

    if (!confirmed) return;

    setIsLoading(true);
    setError(null);

    try {
      // Get operator ID from session storage
      const operatorData = sessionStorage.getItem('current_operator');
      const operatorId = operatorData ? JSON.parse(operatorData).id : undefined;

      const result = await agentClient.invalidateExperienceRun(operatorId);

      if (result.success) {
        // Show success message
        alert('Experience run invalidated successfully. The run will not be billed.');
        if (onRefresh) onRefresh();
      } else {
        setError(result.error || 'Failed to invalidate experience run');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to invalidate experience run');
    } finally {
      setIsLoading(false);
    }
  };

  // Next level - skip to next level in running experience
  const handleNextLevel = async () => {
    if (!agentClient) return;

    const confirmed = window.confirm(
      `Are you sure you want to skip to the next level?\n\nThis will advance the experience to the next level immediately.`
    );

    if (!confirmed) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await agentClient.nextLevel();

      if (result.success) {
        // Show success message briefly
        alert('Next level command sent successfully.');
      } else {
        setError(result.error || 'Failed to send next level command');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send next level command');
    } finally {
      setIsLoading(false);
    }
  };

  // Go to level - open scene picker
  const handleGoToLevel = async () => {
    if (!agentClient || !currentExperience?.id) return;

    setLoadingScenes(true);
    setShowScenePicker(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .schema('experiences')
        .from('experience_scenes')
        .select('index, name')
        .eq('experience_id', currentExperience.id)
        .order('index', { ascending: true });

      if (fetchError) {
        setError('Failed to fetch scenes: ' + fetchError.message);
        setShowScenePicker(false);
        return;
      }

      setScenes(data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch scenes');
      setShowScenePicker(false);
    } finally {
      setLoadingScenes(false);
    }
  };

  // Confirm and execute go to level
  const handleSelectScene = async (scene: { index: number; name: string }) => {
    const confirmed = window.confirm(
      `Are you sure you want to jump to level ${scene.index}: "${scene.name}"?\n\nThis will change the experience immediately.`
    );

    if (!confirmed) return;

    setShowScenePicker(false);
    setIsLoading(true);
    setError(null);

    try {
      const result = await agentClient!.goToLevel(scene.index);

      if (result.success) {
        alert(`Jumped to level ${scene.index}: "${scene.name}" successfully.`);
      } else {
        setError(result.error || 'Failed to send go to level command');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send go to level command');
    } finally {
      setIsLoading(false);
    }
  };

  // Get server-adjusted current time (accounts for clock skew between operator and agent PCs)
  const adjustedNow = () => Date.now() + clockOffset;

  // Format elapsed time
  const getElapsedTime = () => {
    if (!experienceStartTime) return '0:00';

    const elapsed = Math.max(0, Math.floor((adjustedNow() - experienceStartTime) / 1000));
    const minutes = Math.floor(elapsed / 60);
    const seconds = elapsed % 60;

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Calculate and format time remaining
  const getTimeRemaining = () => {
    if (!experienceStartTime || !avgRuntimeSeconds) {
      return 'Calculating...';
    }

    const elapsedSeconds = Math.floor((adjustedNow() - experienceStartTime) / 1000);
    const remainingSeconds = Math.max(0, Math.floor(avgRuntimeSeconds) - elapsedSeconds);

    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Calculate progress percentage for progress bar
  const getProgressPercentage = () => {
    if (!experienceStartTime || !avgRuntimeSeconds) {
      return 0;
    }

    const elapsedSeconds = Math.max(0, Math.floor((adjustedNow() - experienceStartTime) / 1000));
    const percentage = Math.min(100, (elapsedSeconds / avgRuntimeSeconds) * 100);

    return percentage;
  };

  // Set volume
  const handleVolumeChange = async (newVolume: number) => {
    if (!agentClient) return;

    setVolume(newVolume); // Optimistic update

    try {
      const result = await agentClient.setVolume(newVolume);

      if (!result.success) {
        // Revert on error
        const currentVol = await agentClient.getVolume();
        if (currentVol !== null) {
          setVolume(currentVol);
        }
        setError(result.error || 'Failed to set volume');
      }
    } catch (err: any) {
      // Revert on error
      const currentVol = await agentClient.getVolume();
      if (currentVol !== null) {
        setVolume(currentVol);
      }
      setError(err.message || 'Failed to set volume');
    }
  };

  // Trigger update
  const handleTriggerUpdate = async () => {
    if (!agentClient) return;

    const message = forceUpdate
      ? `Are you sure you want to FORCE re-deploy the current version on ${pcInstance.pc_name || 'this PC'}? This will re-download and re-install the agent.`
      : `Are you sure you want to trigger an update on ${pcInstance.pc_name || 'this PC'}?`;

    const confirmed = window.confirm(message);

    if (!confirmed) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await agentClient.triggerUpdate(forceUpdate);

      if (result.success) {
        alert(`Update triggered successfully on ${pcInstance.pc_name || 'PC'}`);
        setForceUpdate(false); // Reset force flag after successful update
        // Refresh update availability status after a few seconds
        setTimeout(async () => {
          try {
            const updateCheck = await agentClient.checkUpdate();
            if (updateCheck.success && updateCheck.data) {
              setUpdateAvailable(updateCheck.data.update_available);
            }
          } catch (err) {
            console.error('Error checking for updates after trigger:', err);
          }
        }, 5000);
      } else {
        setError(result.error || 'Failed to trigger update');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to trigger update');
    } finally {
      setIsLoading(false);
    }
  };

  // Restart RealityAgent service
  const handleRestartAgent = async () => {
    if (!agentClient) return;

    const confirmed = window.confirm(
      `Are you sure you want to restart the RealityAgent service on ${pcInstance.pc_name || 'this PC'}?`
    );

    if (!confirmed) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await agentClient.restartAgent();

      if (result.success) {
        alert(`Agent restart initiated on ${pcInstance.pc_name || 'PC'}`);
      } else {
        setError(result.error || 'Failed to restart agent');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to restart agent');
    } finally {
      setIsLoading(false);
    }
  };

  // Start Parsec (remote-desktop host) — no confirm; starting a host is harmless.
  const handleBootParsec = async () => {
    if (!agentClient) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await agentClient.bootParsec();

      if (result.success) {
        alert(`Parsec start sent to ${pcInstance.pc_name || 'PC'}`);
      } else {
        setError(result.error || 'Failed to start Parsec');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to start Parsec');
    } finally {
      setIsLoading(false);
    }
  };

  // ---- PICO headset actions (via this PC's agent, addressed by serial) ----------
  const hmdTarget = () => pairedHmd?.serial || pairedHmdSerial || null;

  const runHmdAction = async (
    label: string,
    action: (serial: string) => Promise<{ success: boolean; error?: string }>,
    confirmText?: string,
  ) => {
    if (!agentClient) return;
    const serial = hmdTarget();
    if (!serial) {
      setToast({ kind: 'error', message: 'Geen headset gekoppeld aan deze PC' });
      return;
    }
    if (confirmText && !window.confirm(confirmText)) return;
    setHmdBusy(true);
    try {
      const res = await action(serial);
      if (res.success) {
        setToast({ kind: 'success', message: `${label} verstuurd naar headset van ${pcInstance.pc_name || 'PC'}` });
      } else {
        setToast({ kind: 'error', message: `${label} mislukt: ${res.error || 'onbekende fout'}` });
      }
    } finally {
      setHmdBusy(false);
    }
  };

  const handleHmdRestartStreaming = () => runHmdAction('Streaming-herstart', (s) => agentClient!.hmdRestartStreaming(s));
  const [hmdShot, setHmdShot] = useState<HmdScreenshotResult | null>(null);
  const handleHmdScreenshot = () =>
    runHmdAction('Screenshot', async (s) => {
      const res = await agentClient!.hmdScreenshot(s);
      if (res.success && res.data) setHmdShot(res.data);
      return res;
    });
  const handleHmdReboot = () =>
    runHmdAction('Headset-herstart', (s) => agentClient!.hmdReboot(s),
      `Headset van ${pcInstance.pc_name || 'deze PC'} herstarten? De bezoeker verliest ~30 seconden beeld.`);

  // Restart Parsec — confirm, since it drops any active remote session.
  const handleRebootParsec = async () => {
    if (!agentClient) return;

    const confirmed = window.confirm(
      `Restart Parsec on ${pcInstance.pc_name || 'this PC'}? This will drop any active remote session.`
    );

    if (!confirmed) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await agentClient.rebootParsec();

      if (result.success) {
        alert(`Parsec restart sent to ${pcInstance.pc_name || 'PC'}`);
      } else {
        setError(result.error || 'Failed to restart Parsec');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to restart Parsec');
    } finally {
      setIsLoading(false);
    }
  };

  // Shutdown PC
  const handleShutdownPC = async () => {
    if (!agentClient) return;

    const confirmed = window.confirm(
      `Are you sure you want to SHUTDOWN ${pcInstance.pc_name || 'this PC'}? This will turn off the computer.`
    );

    if (!confirmed) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await agentClient.shutdown();

      if (result.success) {
        alert(`Shutdown command sent to ${pcInstance.pc_name || 'PC'}`);
      } else {
        setError(result.error || 'Failed to shutdown PC');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to shutdown PC');
    } finally {
      setIsLoading(false);
    }
  };

  // Restart PC
  const handleRestartPC = async () => {
    if (!agentClient) return;

    const confirmed = window.confirm(
      `Are you sure you want to RESTART ${pcInstance.pc_name || 'this PC'}?`
    );

    if (!confirmed) return;

    setIsLoading(true);
    setError(null);

    try {
      // Route through the agent client so it gets LAN/Tailscale failover too.
      const result = await agentClient.restartPC();

      if (result.success) {
        alert(`Restart command sent to ${pcInstance.pc_name || 'PC'}`);
      } else {
        setError(result.error || 'Failed to restart PC');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to restart PC');
    } finally {
      setIsLoading(false);
    }
  };

  // "Attempt auto repair" — generic over the notification's policy-decorated
  // category. The agent runs that category's DB repair sequence and verifies it;
  // it returns immediately and the outcome (recovered / needs_reboot) arrives over
  // the websocket and updates repairStatus (see the 'repair_progress' handler).
  // Start the agent's repair sequence. The repair needs an idle PC (it verifies by
  // launching a boot test), and a running experience blocks it agent-side — which
  // is every no-VR alert, so we confirm and pass explicit consent to stop the
  // session first. Without this the button appeared to work and did nothing.
  const handleAttemptRepair = async (notification: Notification) => {
    if (!agentClient) return;
    const category = notification.category || 'experience_no_vr';
    const mustStop = !!currentExperience;
    if (mustStop) {
      const ok = window.confirm(
        `Auto-herstel stopt de lopende ervaring "${currentExperience?.name || ''}" op ${pcInstance.pc_name || 'deze PC'} en probeert de streaming te herstellen. Doorgaan?`
      );
      if (!ok) return;
    }
    setRepairStatus({ category, phase: 'started', running: true, recovered: false, needsReboot: false, message: 'Auto-herstel gestart…' });
    const res = await agentClient.repairRecover(category, mustStop);
    if (!res.success) {
      setRepairStatus({ category, phase: 'blocked', running: false, recovered: false, needsReboot: false, message: res.error || 'Kon niet starten' });
      setToast({ kind: 'error', message: `Auto-herstel kon niet starten: ${res.error || ''}` });
    }
  };

  // Dismiss a sticky banner. Honours the policy's confirm_dismiss. Hides it locally
  // at once, then clears it agent-side so the 8s status poll won't re-show it; a
  // fresh failure re-fires it.
  const handleDismissNotification = async (notification: Notification) => {
    if (notification.confirm_dismiss) {
      const ok = window.confirm(`Melding sluiten op ${pcInstance.pc_name || 'deze PC'}?`);
      if (!ok) return;
    }
    notificationsRef.current = notificationsRef.current.filter((n) => n.id !== notification.id);
    setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
    if (repairStatus?.category === (notification.category || 'experience_no_vr')) setRepairStatus(null);
    if (agentClient) await agentClient.stopNotification(notification.id);
  };

  // Reboot after a failed auto repair (the visible escalation). Honours the
  // policy's confirm_reboot (default: confirm unless explicitly false).
  const handleRepairReboot = async (notification: Notification) => {
    if (!agentClient) return;
    if (notification.confirm_reboot !== false) {
      const confirmed = window.confirm(
        `Auto-herstel is mislukt op ${pcInstance.pc_name || 'deze PC'}. PC nu herstarten?`
      );
      if (!confirmed) return;
    }
    const res = await agentClient.restartPC();
    if (res.success) {
      setToast({ kind: 'success', message: `Herstart verstuurd naar ${pcInstance.pc_name || 'PC'}` });
      setRepairStatus(null);
    } else {
      setToast({ kind: 'error', message: `Herstart mislukt: ${res.error || ''}` });
    }
  };

  // Which action controls a notification offers. The agent decorates policy-driven
  // notifications with `actions`; older agents don't, so the no-VR banner keeps its
  // legacy repair+dismiss set. Anything else with no actions shows none.
  const notificationActions = (n: Notification): string[] => {
    if (n.actions && n.actions.length) return n.actions;
    if (n.id === VR_NO_STREAM_NOTIFICATION_ID) return ['repair', 'dismiss'];
    return [];
  };

  // Render the policy-decorated action controls for a notification (generic over
  // category/actions/confirm flags — no per-incident-type UI code).
  const renderNotificationActions = (notification: Notification) => {
    const acts = notificationActions(notification);
    if (acts.length === 0) return null;
    const rs =
      repairStatus && repairStatus.category === (notification.category || 'experience_no_vr')
        ? repairStatus
        : null;
    const canRepairNow = !rs || (!rs.running && !rs.needsReboot && !rs.recovered);
    return (
      <span className="notification-action-v2">
        {acts.includes('repair') && canRepairNow && (
          <button className="notification-repair-btn" onClick={() => handleAttemptRepair(notification)}>
            🔧 Auto-herstel
          </button>
        )}
        {rs?.running && <span className="notification-repair-progress">⏳ Herstellen… ({rs.phase})</span>}
        {rs && !rs.running && rs.recovered && <span className="notification-repair-ok">✓ Hersteld</span>}
        {rs && !rs.running && rs.needsReboot && (
          <button className="notification-repair-btn notification-repair-reboot" onClick={() => handleRepairReboot(notification)}>
            ⟳ Herstel mislukt — herstart PC
          </button>
        )}
        {acts.includes('dismiss') && !rs?.running && (
          <button className="notification-repair-btn notification-dismiss-btn" onClick={() => handleDismissNotification(notification)} title="Sluiten">
            ✕
          </button>
        )}
      </span>
    );
  };

  // Capture an incident: the agent bundles this PC's logs to storage and records
  // a row in operations.incidents. Non-destructive diagnostic action. Fires
  // immediately on click (no prompt — window.prompt is a no-op under Electron).
  const handleCaptureIncident = async () => {
    if (!agentClient) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await agentClient.captureIncident();

      if (result.success) {
        const id = result.data?.id;
        alert(`Incident captured on ${pcInstance.pc_name || 'PC'}${id ? ` (id ${id})` : ''}. Logs are uploading.`);
      } else {
        setError(result.error || 'Failed to capture incident');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to capture incident');
    } finally {
      setIsLoading(false);
    }
  };

  // --- Autodiagnostics actions ---

  // Start a self-test run ("Retry checks" / "Run checks"). With auto_run
  // gated off agent-side, manual triggers like this are the only way runs
  // start until the owner approves automatic ones.
  const handleRunDiagnostics = async () => {
    if (!agentClient) return;
    setIsLoading(true);
    try {
      const result = await agentClient.runDiagnostics();
      if (result.success) {
        setToast({ kind: 'success', message: `Checks started on ${pcInstance.pc_name || 'PC'}` });
      } else if (result.error === 'run_active') {
        setToast({ kind: 'error', message: 'Checks are already running' });
      } else if (result.error === 'experience_running') {
        setToast({ kind: 'error', message: 'Cannot run checks while an experience is running' });
      } else {
        setToast({ kind: 'error', message: result.error || 'Failed to start checks' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Abort the active run ("Skip checks").
  const handleAbortDiagnostics = async () => {
    if (!agentClient) return;
    setIsLoading(true);
    try {
      const result = await agentClient.abortDiagnostics();
      if (!result.success) {
        setToast({ kind: 'error', message: result.error || 'Failed to skip checks' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Override a malfunctioning verdict. Called from DiagnosticsOverrideModal —
  // must not throw (the modal relies on that, like MarkIssueModal).
  const handleDiagnosticsOverrideSubmit = async (operator: string, reason: string) => {
    if (!agentClient) {
      setShowDiagOverride(false);
      return;
    }
    const result = await agentClient.overrideDiagnostics(operator, reason);
    setShowDiagOverride(false);
    if (result.success) {
      setToast({ kind: 'success', message: `Checks overridden on ${pcInstance.pc_name || 'PC'}` });
    } else {
      setToast({ kind: 'error', message: result.error || 'Failed to override checks' });
    }
  };

  // Prefill for the override modal's operator field, from the logged-in
  // operator (same sessionStorage identity the launch/kill flows use).
  const currentOperatorName = (): string => {
    try {
      const operatorData = sessionStorage.getItem('current_operator');
      if (!operatorData) return '';
      const operator: Operator = JSON.parse(operatorData);
      return operator.name || operator.operator_code || '';
    } catch {
      return '';
    }
  };

  // ── Sync pause/resume (swarm build distribution) ───────────────────────────
  // DB flag first (durable intent — the operator is the single writer of
  // pc_instances.sync_paused), then poke the agent for immediate effect.
  // A 404 on the poke = old agent without sync control (the flag suffices).
  const handlePauseSync = async () => {
    if (!agentClient) return;
    const operator = currentOperatorName();
    const dbOk = await databaseService.setSyncPaused([pcInstance.id], true, operator || undefined);
    if (!dbOk) {
      setToast({ kind: 'error', message: 'Pauze-vlag niet opgeslagen — sync kan na een agent-herstart hervatten' });
    }
    await agentClient.pauseSync(operator || undefined);
    setToast({ kind: 'success', message: 'Sync gepauzeerd — voortgang blijft bewaard' });
  };

  const handleResumeSync = async () => {
    if (!agentClient) return;
    const operator = currentOperatorName();
    await databaseService.setSyncPaused([pcInstance.id], false, operator || undefined);
    await agentClient.resumeSync(operator || undefined);
    setToast({ kind: 'success', message: 'Sync hervat' });
  };

  // One-click "Pauzeer sync & start": pause (piece bitmap keeps ALL progress)
  // then the normal launch flow. Deterministic: the agent sets its pause cache
  // synchronously in the poke, so the launch guard passes — paused always wins.
  const handlePauseSyncAndLaunch = async (release: any) => {
    if (!agentClient) return;
    const operator = currentOperatorName();
    const dbOk = await databaseService.setSyncPaused([pcInstance.id], true, operator || undefined);
    if (!dbOk) {
      // Non-fatal: the customer comes first; the pause just won't survive an
      // agent restart until the flag write succeeds.
      setToast({ kind: 'error', message: 'Pauze-vlag niet opgeslagen — sync kan na een agent-herstart hervatten' });
    }
    const poke = await agentClient.pauseSync(operator || undefined);
    if (!poke.success && poke.error !== 'sync control unavailable') {
      // Real transport failure — the launch would fail identically, stop here.
      setToast({ kind: 'error', message: `PC niet bereikbaar: ${poke.error}` });
      return;
    }
    handleExperienceClick(release);
  };

  // Elapsed time since the diagnostics run started (server-clock adjusted).
  const getDiagElapsed = () => {
    if (!diagnostics?.started_at) return '';
    const elapsed = Math.max(0, Math.floor((adjustedNow() - new Date(diagnostics.started_at).getTime()) / 1000));
    const minutes = Math.floor(elapsed / 60);
    const seconds = elapsed % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Context line for the Mark Issue modal, e.g.
  // "CLEOPATRA-03 · Egypt v1.4 · scene 3 (Tomb)".
  const markIssueContext = () => {
    const parts: string[] = [pcInstance.pc_name || `PC ${pcInstance.player_index ?? '?'}`];
    if (currentExperience) {
      parts.push(
        `${currentExperience.name}${currentExperience.version ? ` v${currentExperience.version}` : ''}`
      );
      if (currentExperience.current_level !== undefined) {
        parts.push(
          `scene ${currentExperience.current_level + 1}${currentExperience.scene_name ? ` (${currentExperience.scene_name})` : ''}`
        );
      } else if (currentExperience.scene_name) {
        parts.push(currentExperience.scene_name);
      }
    }
    return parts.join(' · ');
  };

  // Mark an issue on this PC. Preferred path: the agent's POST /api/incidents,
  // which attaches run/scene context and uploads a log bundle. If the agent is
  // unreachable, fall back to a direct operations.incidents insert so the
  // marker is never lost. Never throws (MarkIssueModal relies on that).
  const handleMarkIssueSubmit = async ({ category, severity, note }: MarkIssueSubmit) => {
    const pcName = pcInstance.pc_name || `PC ${pcInstance.player_index ?? '?'}`;
    let operatorId: string | undefined;
    try {
      const operatorData = sessionStorage.getItem('current_operator');
      operatorId = operatorData ? JSON.parse(operatorData).id : undefined;
    } catch {
      operatorId = undefined;
    }
    // Old-agent bridge: prefix the note with the category label so the incident
    // message stays readable even when a pre-release agent drops the (unknown)
    // category field.
    const message = note ? `[${category.label}] ${note}` : `[${category.label}]`;

    if (agentClient && isOnline) {
      try {
        const result = await agentClient.captureIncident({
          note: message,
          severity,
          category: category.key,
          operatorId,
        });
        if (result.success) {
          setShowMarkIssue(false);
          setToast({ kind: 'success', message: `Issue marked on ${pcName} — logs uploading` });
          return;
        }
      } catch {
        // Fall through to the direct-insert fallback.
      }
    }

    const ok = await databaseService.insertIncident({
      severity,
      source: 'operator',
      message,
      pc_instance_id: pcInstance.id,
      room_id: roomId ?? null,
      experience_id: currentExperience?.id ?? null,
      metadata: {
        category: category.key,
        note,
        operator_id: operatorId ?? null,
        agent_unreachable: true,
      },
    });

    setShowMarkIssue(false);
    if (ok) {
      setToast({
        kind: 'success',
        message: `Issue marked on ${pcName} — saved directly (agent unreachable)`,
      });
    } else {
      setToast({ kind: 'error', message: `Failed to mark issue on ${pcName}` });
    }
  };

  // Send a preset voice warning to the visitor in the headset. The agent
  // responds "playing" immediately and reports completion over its WebSocket.
  // Never throws (SendVisitorMessageModal relies on that).
  const handleSendVisitorMessageSubmit = async (msg: OperatorMessage) => {
    const pcName = pcInstance.pc_name || `PC ${pcInstance.player_index ?? '?'}`;
    if (!agentClient || !isOnline) {
      setShowSendMessage(false);
      setToast({ kind: 'error', message: `${pcName} is niet bereikbaar` });
      return;
    }
    const result = await agentClient.sendVisitorMessage({
      key: msg.key,
      text_nl: msg.text_nl,
      text_en: msg.text_en,
      clip_version: msg.clip_version,
      operator: currentOperatorName(),
    });
    setShowSendMessage(false);
    if (result.success) {
      setToast({ kind: 'success', message: `"${msg.label}" wordt afgespeeld op ${pcName}` });
    } else {
      setToast({ kind: 'error', message: result.error || `Bericht versturen naar ${pcName} mislukt` });
    }
  };

  // Helper to render the Mark Issue modal + toast (shared across tile states)
  const renderMarkIssueExtras = () => (
    <>
      {showMarkIssue && (
        <MarkIssueModal
          contextLine={markIssueContext()}
          onCancel={() => setShowMarkIssue(false)}
          onSubmit={handleMarkIssueSubmit}
        />
      )}
      {showHmdSheet && (
        <HeadsetSheet
          pcName={pcInstance.pc_name || 'PC'}
          hmd={pairedHmd}
          pairedSerial={pairedHmdSerial}
          streamingConnected={picoStatus?.connected === true}
          isAdmin={isAdmin}
          canRestartStreaming={canRestartHmdStreaming}
          canReboot={canRebootHmd}
          busy={hmdBusy}
          onRestartStreaming={handleHmdRestartStreaming}
          onReboot={handleHmdReboot}
          onScreenshot={handleHmdScreenshot}
          screenshot={hmdShot}
          shadowFloor={mpShadowFloor}
          boundary={hmdBoundary}
          onHmdProxy={pairedHmd?.serial && agentClient ? (fn, args) => agentClient.hmdProxy(pairedHmd.serial, fn, args) : undefined}
          onHmdSwitch={pairedHmd?.serial && agentClient ? (fn, on) => agentClient.hmdSwitch(pairedHmd.serial, fn, on) : undefined}
          onClose={() => { setShowHmdSheet(false); setHmdShot(null); }}
        />
      )}
      {showSendMessage && (
        <SendVisitorMessageModal
          contextLine={markIssueContext()}
          onCancel={() => setShowSendMessage(false)}
          onSubmit={handleSendVisitorMessageSubmit}
        />
      )}
      {toast && <Toast message={toast.message} kind={toast.kind} onClose={() => setToast(null)} />}
    </>
  );

  // Handle Wake-on-LAN for offline PC
  const handleWakeOnLAN = async () => {
    if (!pcInstance.mac_address) {
      alert('Geen MAC adres beschikbaar');
      return;
    }

    // Both wake paths — local magic packet plus the DB flag for peer agents.
    // Shared with Dagstart and "Zet alle computers aan" so the three cannot drift;
    // see services/wake.ts for why neither path alone is sufficient.
    setIsLoading(true);
    try {
      const ok = await wakeMachines([pcInstance]);

      if (ok) {
        setWakeRequested(true);
      } else {
        alert('Computer aanzetten mislukt. Probeer het opnieuw.');
      }
    } catch (error) {
      console.error('Wake request error:', error);
      alert('Computer aanzetten mislukt. Probeer het opnieuw.');
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-revert the "waking" state after the wake-request TTL (10 min) so the button
  // becomes retryable if the PC never came up. If it does come up, the tile switches
  // to the online view and this state no longer matters.
  useEffect(() => {
    if (!wakeRequested) return;
    const timer = setTimeout(() => setWakeRequested(false), 10 * 60 * 1000);
    return () => clearTimeout(timer);
  }, [wakeRequested]);

  // Helper to render the header row (used in all tile states)
  const renderHeader = () => (
    <div className="pc-tile-header-v2">
      <span className="player-index">{displayName}</span>
      <div className="device-batteries">
        <span
          className={`battery-indicator ${!picoStatus?.connected ? 'disconnected' : picoStatus?.charging === true ? 'charging' : (picoStatus?.battery ?? 100) < 20 ? 'low' : ''} ${picoStatus?.connected && picoStatus?.charging === false ? 'discharging' : ''} ${hmdAgentOnline ? 'hmd-tappable' : ''} ${hmdStreamingDown ? 'hmd-streaming-down' : ''}`}
          onClick={(e) => { if (hmdAgentOnline || pairedHmdSerial) { e.stopPropagation(); setShowHmdSheet(true); } }}
          title={hmdAgentOnline ? 'Headset' : undefined}
        >
          BRIL {picoStatus?.connected ? `${picoStatus.battery ?? '--'}%` : hmdAgentOnline && pairedHmd?.battery != null ? `${pairedHmd.battery}%` : '--'}
          {picoStatus?.charging === true && <span className="charging-indicator">↑</span>}
          {picoStatus?.charging === false && <span className="discharging-indicator">↓</span>}
          {hmdAgentOnline && <span className={`hmd-dot ${hmdStreamingDown ? 'down' : 'up'}`} aria-hidden="true" />}
        </span>
        <span className={`battery-indicator ${!headsetStatus?.connected ? 'disconnected' : (headsetBattery ?? 100) < 20 ? 'low' : ''}`}>
          HP {headsetStatus?.connected ? `${headsetBattery ?? '--'}%` : '--'}
        </span>
        {hmdBoundary?.state === 'failed' && (
          <span
            className="battery-indicator low hmd-tappable"
            onClick={(e) => { e.stopPropagation(); setShowHmdSheet(true); }}
            title="De headset meldt geen speelgrens en de automatische herscan hielp niet. Start de ervaring (opnieuw) of herstart de headset."
          >
            GEEN SPEELGRENS
          </span>
        )}
        {hmdBoundary?.state === 'rescanning' && (
          <span className="battery-indicator" title="Speelgrens weg; de headset herscant de ruimte">SPEELGRENS…</span>
        )}
      </div>
      {showRedButton && (
        <button
          className="red-circle-btn"
          onClick={() => setShowRedPopup(true)}
          title="PC Controls"
        >
          <span className="red-circle" />
        </button>
      )}
    </div>
  );

  // Helper to render the red button popup overlay
  const renderRedPopup = () => showRedPopup && (
    <div className="red-button-popup-overlay" onClick={() => setShowRedPopup(false)}>
      <div className="red-popup-content" onClick={(e) => e.stopPropagation()}>
        <button className="red-popup-close" onClick={() => setShowRedPopup(false)}>✕</button>
        {canSkipBilling && (
          <label className="red-popup-toggle">
            <input
              type="checkbox"
              checked={skipBilling}
              onChange={(e) => setSkipBilling(e.target.checked)}
            />
            <span>Mark as Test Run</span>
          </label>
        )}
        {canShutdownPC && (
          <button className="red-popup-btn shutdown" onClick={() => { setShowRedPopup(false); handleShutdownPC(); }} disabled={isLoading}>
            Shutdown PC
          </button>
        )}
        {canRestartPC && (
          <button className="red-popup-btn restart-pc" onClick={() => { setShowRedPopup(false); handleRestartPC(); }} disabled={isLoading}>
            Restart PC
          </button>
        )}
        {canRestartAgent && (
          <button className="red-popup-btn restart-agent" onClick={() => { setShowRedPopup(false); handleRestartAgent(); }} disabled={isLoading}>
            Restart Agent
          </button>
        )}
        {canControlParsec && (
          <button className="red-popup-btn start-parsec" onClick={() => { setShowRedPopup(false); handleBootParsec(); }} disabled={isLoading}>
            Start Parsec
          </button>
        )}
        {canControlParsec && (
          <button className="red-popup-btn restart-parsec" onClick={() => { setShowRedPopup(false); handleRebootParsec(); }} disabled={isLoading}>
            Restart Parsec
          </button>
        )}
        {canMarkIssue && (
          <button className="red-popup-btn mark-issue" onClick={() => { setShowRedPopup(false); setShowMarkIssue(true); }} disabled={isLoading}>
            Mark Issue
          </button>
        )}
        {canSendVisitorMessage && (
          <button className="red-popup-btn send-message" onClick={() => { setShowRedPopup(false); setShowSendMessage(true); }} disabled={isLoading || !isOnline}>
            Bericht naar bezoeker
          </button>
        )}
        {canCaptureIncident && (
          <button className="red-popup-btn capture-incident" onClick={() => { setShowRedPopup(false); handleCaptureIncident(); }} disabled={isLoading}>
            Capture Logs
          </button>
        )}
      </div>
    </div>
  );

  // Helper to render the volume footer
  const renderVolumeFooter = () => isOnline && (
    <div className="pc-tile-footer">
      <div className="volume-control-v2">
        <span className="volume-label">VOLUME</span>
        <input
          type="range"
          min="0"
          max="100"
          value={volume}
          onChange={(e) => handleVolumeChange(parseInt(e.target.value))}
          disabled={!isOnline}
          style={{ '--value': `${volume}%` } as React.CSSProperties}
        />
      </div>
    </div>
  );

  // Helper to render the diagnostics override + report modals (online tile only)
  const renderDiagModals = () => (
    <>
      {showDiagOverride && (
        <DiagnosticsOverrideModal
          contextLine={pcInstance.pc_name || `PC ${pcInstance.player_index ?? '?'}`}
          defaultOperator={currentOperatorName()}
          onCancel={() => setShowDiagOverride(false)}
          onSubmit={handleDiagnosticsOverrideSubmit}
        />
      )}
      {showDiagReport && agentClient && (
        <DiagnosticsReportModal
          contextLine={pcInstance.pc_name || `PC ${pcInstance.player_index ?? '?'}`}
          client={agentClient}
          onClose={() => setShowDiagReport(false)}
        />
      )}
    </>
  );

  // Compact diagnostics chips/badges under the header: passed check, degraded
  // dot, pending-HMD chip, override badge, and the report_only "would be
  // blocked" alarm. Nothing renders for off/idle/aborted/undefined.
  const renderDiagChips = () => {
    if (!diagnostics) return null;
    const chips: React.ReactNode[] = [];
    const finishedAt = diagnostics.last_result?.finished_at;
    const finishedLabel = finishedAt
      ? new Date(finishedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : '';

    if (diagState === 'passed') {
      // Clickable (operators too, not just admins): opens the full report.
      chips.push(
        <button
          key="pass"
          className="diag-chip pass diag-chip-btn"
          title={`Checks passed${finishedLabel ? ` at ${finishedLabel}` : ''} — click for report`}
          onClick={() => setShowDiagReport(true)}
        >
          ✓ Checks
        </button>
      );
    } else if (diagState === 'degraded_pass') {
      const advisory = diagnostics.last_result?.advisory_failed || [];
      const skipped = diagnostics.last_result?.skipped || [];
      const tooltip = [
        advisory.length > 0 ? `Advisory failed: ${advisory.map(humanizeDiagId).join(', ')}` : '',
        skipped.length > 0 ? `Skipped: ${skipped.map(humanizeDiagId).join(', ')}` : '',
      ].filter(Boolean).join('\n') || 'Passed with advisories';
      // Clickable (operators too): "why is this amber?" → the full report.
      chips.push(
        <button
          key="degraded"
          className="diag-chip degraded diag-chip-btn"
          title={`${tooltip}\nClick for report`}
          onClick={() => setShowDiagReport(true)}
        >
          ● Checks degraded
        </button>
      );
    } else if (diagState === 'overridden') {
      const by = diagnostics.override?.by || 'unknown';
      const at = diagnostics.override?.at
        ? new Date(diagnostics.override.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : '';
      chips.push(
        <span key="override" className="diag-badge-overridden" title={diagnostics.override?.reason || ''}>
          Checks overridden — {by}{at ? ` ${at}` : ''}
        </span>
      );
    }

    if ((diagState === 'passed' || diagState === 'degraded_pass') && diagPendingHmd.length > 0) {
      chips.push(
        <span key="pending-hmd" className="diag-chip pending-hmd" title={diagPendingHmd.map(humanizeDiagId).join(', ')}>
          Boot check pending — connect headset
        </span>
      );
    }

    if (diagState === 'malfunctioning' && diagnostics.enforcement === 'report_only') {
      // report_only: loud alarm badge, but the launch UI stays usable.
      chips.push(
        <span key="would-block" className="diag-badge-would-block">
          ⚠ CHECKS FAILED — would be blocked
          <button className="diag-badge-btn" onClick={handleRunDiagnostics} disabled={isLoading}>
            Retry
          </button>
          <button className="diag-badge-btn" onClick={() => setShowDiagReport(true)} disabled={isLoading}>
            Report
          </button>
        </span>
      );
    }

    if (chips.length === 0) return null;
    return <div className="diag-chip-row">{chips}</div>;
  };

  // Failing blocking tests with their failure messages, for the red tile.
  const diagBlockingFailures = () => {
    const blocking = diagnostics?.last_result?.blocking_failed || [];
    const tests = diagnostics?.tests || [];
    if (blocking.length > 0) {
      return blocking.map((id) => {
        const test = tests.find((t) => t.id === id);
        return { id, message: test?.message || test?.failure_code || 'failed' };
      });
    }
    // Fallback while last_result hasn't landed yet: derive from tests[].
    return tests
      .filter((t) => t.severity === 'blocking' && t.status === 'fail')
      .map((t) => ({ id: t.id, message: t.message || t.failure_code || 'failed' }));
  };

  // Full-body content while the agent is running checks or applying a fix.
  const renderDiagRunningBody = () => {
    const progress = diagnostics?.progress;
    const fix = diagnostics?.current_fix;
    return (
      <div className={`diag-run-body ${diagState === 'fixing' ? 'fixing' : 'testing'}`}>
        {diagState === 'fixing' && fix ? (
          <>
            <div className="diag-run-title fixing">
              Fixing: {humanizeDiagId(fix.fix_id)} ({fix.attempt}/{fix.max})
            </div>
            {progress && (
              <div className="diag-run-subtitle">
                Checks {progress.done}/{progress.total}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="diag-run-title">
              Running checks {progress ? `${progress.done}/${progress.total}` : ''}
            </div>
            {progress?.current_test && (
              <div className="diag-run-subtitle">{humanizeDiagId(progress.current_test)}</div>
            )}
          </>
        )}
        {diagnostics?.started_at && <div className="diag-run-elapsed">{getDiagElapsed()}</div>}
        <div className="diag-run-actions">
          <button className="diag-skip-btn" onClick={handleAbortDiagnostics} disabled={isLoading}>
            Skip checks
          </button>
          {/* Mid-run report: the endpoint returns the in-progress run detail */}
          <button className="diag-skip-btn" onClick={() => setShowDiagReport(true)}>
            View report
          </button>
        </div>
      </div>
    );
  };

  // Sync chips: the loud sticky PAUSED badge (shown in every online state,
  // even idle — a forgotten pause must be impossible to miss), the passive
  // report_only "syncing" chip with a pause action, nothing for deferred
  // (the running-experience UI owns the tile then).
  const renderSyncChips = () => {
    if (!syncStatus) return null;
    const chips: React.ReactNode[] = [];

    if (syncPaused) {
      chips.push(
        <span key="sync-paused" className="sync-chip sync-chip-paused">
          ⏸ SYNC GEPAUZEERD{syncStatus.paused.by ? ` — ${syncStatus.paused.by}` : ''}
          <button className="sync-chip-btn" onClick={handleResumeSync} disabled={isLoading}>
            Hervat
          </button>
        </span>
      );
    } else if (syncBusy && syncStatus.gate === 'report_only' && !currentExperience) {
      const p = syncStatus.pulling;
      const label = p.active
        ? `Synct ${p.experience || 'content'}${typeof p.pct === 'number' ? ` · ${p.pct}%` : ''}`
        : `Seedt naar ${syncStatus.serving.connections} PC${syncStatus.serving.connections === 1 ? '' : "'s"}`;
      chips.push(
        <span key="sync-busy" className="sync-chip sync-chip-busy">
          ⇅ {label}
          <button className="sync-chip-btn" onClick={handlePauseSync} disabled={isLoading}>
            Pauzeer
          </button>
        </span>
      );
    }

    if (chips.length === 0) return null;
    return <div className="diag-chip-row">{chips}</div>;
  };

  // Full-body takeover while sync blocks launches (gate=block): progress,
  // peer detail, and the launch grid as one-click "Pauzeer sync & start"
  // buttons (pause keeps all progress via the persisted piece bitmap).
  const renderSyncBody = () => {
    const p = syncStatus?.pulling;
    const s = syncStatus?.serving;
    return (
      <div className="sync-run-body">
        <div className="sync-run-title">⇅ Content synchroniseren</div>
        {p?.active && (
          <>
            <div className="sync-run-subtitle">
              {p.experience || 'content'}
              {p.version ? ` v${p.version}` : ''}
              {p.source === 's3'
                ? ' · vanaf cloud'
                : typeof p.peers === 'number'
                  ? ` · ${p.peers} peer${p.peers === 1 ? '' : 's'}${p.upstream_peers ? ` (+${p.upstream_peers} upstream)` : ''}`
                  : ''}
            </div>
            <div className="launch-progress-container-v2">
              <div className="launch-progress-bar-v2">
                <div className="launch-progress-fill-v2" style={{ width: `${p.pct ?? 0}%` }} />
              </div>
              <span className="launch-progress-percent-v2">{p.pct ?? 0}%</span>
            </div>
          </>
        )}
        {s?.active && (
          <div className="sync-run-subtitle">
            Seedt naar {s.connections} verbinding{s.connections === 1 ? '' : 'en'}
          </div>
        )}
        {experiences.length > 0 && (
          <>
            <div className="sync-run-launch-label">Pauzeer sync & start:</div>
            <div className={`experience-launch-grid-v2${experiences.length > 2 ? ' compact' : ''}`}>
              {experiences.map((release: any) => {
                const expColor = nameToColor(release.experiences?.name || 'Unknown');
                const colorStyle = { '--btn-color': expColor.border, '--btn-bg': expColor.bg } as React.CSSProperties;
                return (
                  <button
                    key={release.id}
                    className="launch-btn-v2"
                    style={colorStyle}
                    onClick={() => handlePauseSyncAndLaunch(release)}
                    disabled={isLoading}
                  >
                    <span className="launch-btn-name-v2">⏸▶ {release.experiences?.name || 'Unknown'}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  };

  // Full-body content when blocking checks failed and enforcement blocks launches.
  const renderDiagMalfunctioningBody = () => (
    <div className="diag-malfunction-body">
      <div className="diag-malfunction-title">⚠ Checks failed</div>
      <div className="diag-malfunction-list">
        {diagBlockingFailures().map((failure) => (
          <div key={failure.id} className="diag-malfunction-item">
            <span className="diag-malfunction-test">{humanizeDiagId(failure.id)}:</span> {failure.message}
          </div>
        ))}
      </div>
      <div className="diag-malfunction-actions">
        <button className="diag-action-btn retry" onClick={handleRunDiagnostics} disabled={isLoading}>
          Retry checks
        </button>
        <button className="diag-action-btn override" onClick={() => setShowDiagOverride(true)} disabled={isLoading}>
          Override & unlock
        </button>
        <button className="diag-action-btn report" onClick={() => setShowDiagReport(true)} disabled={isLoading}>
          View report
        </button>
      </div>
    </div>
  );

  // Show error state if MP is disconnected in non-admin mode
  if (!isAdmin && mpConnected === false) {
    return (
      <div className="pc-tile mp-error-tile">
        {renderRedPopup()}
        {renderMarkIssueExtras()}
        {renderHeader()}
        <div className="pc-tile-body">
          <div className="mp-error-content">
            <div className="mp-error-icon">⚠</div>
            <div className="mp-error-message">Multiplayer Not Connected</div>
          </div>
        </div>
        {renderVolumeFooter()}
      </div>
    );
  }

  // Show offline state. Instead of the old binary "agent didn't answer ⇒ off",
  // derive a lifecycle state from how long /api/status has been failing plus the
  // agent's pre-shutdown intent (freshly re-fetched into dbRow while unreachable):
  //   reconnecting → brief blip / service bounce / start of a reboot (no verdict)
  //   restarting   → agent announced a reboot (status) or diagnostics self-repair
  //   updating     → agent announced a self-update
  //   unreachable  → grace exceeded, no intent — may be booting; not proven off
  //   off          → deliberate shutdown, or long-dead heartbeat, or timeout
  // The 3–5 min timeouts are what turn a stuck restart back into "off".
  // Auto-repair countdown helpers (reboot-retry). Remaining runs against the
  // agent's clock (started_at came from the agent; adjustedNow() carries the
  // measured skew). Used by both the online takeover body and the offline
  // restarting tile — the countdown must keep running while the PC is dark.
  const autoRepairRemainingS = (): number | null => {
    if (!autoRepair) return null;
    return Math.round(autoRepair.estimateS - (adjustedNow() - autoRepair.startedAtMs) / 1000);
  };
  const formatCountdown = (totalS: number): string => {
    const m = Math.floor(totalS / 60);
    const s = totalS % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  const autoRepairCountdownText = (): string => {
    const rem = autoRepairRemainingS();
    if (rem === null) return '';
    return rem > 0 ? `nog ~${formatCountdown(rem)}` : 'duurt langer dan verwacht…';
  };
  // Live = still worth showing. Far past budget (verdict never arrived — agent
  // died mid-repair) it stops claiming anything.
  const AUTO_REPAIR_OVERRUN_GRACE_S = 10 * 60;
  const autoRepairLive =
    autoRepair !== null && (autoRepairRemainingS() ?? -Infinity) > -AUTO_REPAIR_OVERRUN_GRACE_S;

  if (!isOnline) {
    const now = Date.now();
    const view = dbRow ?? pcInstance; // dbRow is fresh; the prop is stale-on-load
    const st = view.status;
    const diagState = dbRow?.diag_state ?? pcInstance.diag_state;
    const hbAgeMs = view.last_heartbeat
      ? now - new Date(view.last_heartbeat).getTime()
      : Number.POSITIVE_INFINITY;
    const diagAgeMs = view.diag_updated_at
      ? now - new Date(view.diag_updated_at).getTime()
      : Number.POSITIVE_INFINITY;
    const unreachableMs = now - lastReachableAtRef.current;

    const RECONNECT_GRACE_MS = 90 * 1000; // brief blip before we say anything
    const OFF_TIMEOUT_MS = 5 * 60 * 1000; // unreachable/stuck-intent → off
    const SELF_REPAIR_TIMEOUT_MS = 10 * 60 * 1000; // matches the agent reboot budget window

    type LifecycleKind = 'reconnecting' | 'restarting' | 'updating' | 'unreachable' | 'off';
    let kind: LifecycleKind;
    let selfRepair = false;
    let agentRestart = false; // agent service bounce (PC stays on) vs a full PC reboot
    if (st === 'shutting_down') {
      kind = 'off'; // deliberate power-off — no point pretending it will return
    } else if (diagState === 'rebooting' && diagAgeMs < SELF_REPAIR_TIMEOUT_MS) {
      kind = 'restarting';
      selfRepair = true;
    } else if (st === 'restarting' && hbAgeMs < OFF_TIMEOUT_MS) {
      kind = 'restarting';
    } else if (st === 'restarting_agent' && hbAgeMs < OFF_TIMEOUT_MS) {
      kind = 'restarting';
      agentRestart = true;
    } else if (st === 'updating' && hbAgeMs < OFF_TIMEOUT_MS) {
      kind = 'updating';
    } else if (hbAgeMs > OFF_TIMEOUT_MS) {
      // Heartbeat long dead → genuinely off (also the already-off-on-load case).
      // "Off" is driven by heartbeat death, not by the operator's reach: a PC that
      // is still heartbeating to Supabase but whose /api/status we can't reach is
      // partitioned, not off, so it stays "unreachable" below rather than flipping.
      kind = 'off';
    } else if (unreachableMs < RECONNECT_GRACE_MS) {
      kind = 'reconnecting';
    } else {
      kind = 'unreachable';
    }

    const wakeRow = wakeRequested ? (
      <>
        <div className="offline-message">Computer wordt opgestart…</div>
        <button className="wol-btn" onClick={handleWakeOnLAN} disabled={isLoading}>
          {isLoading ? 'Bezig...' : 'Opnieuw proberen'}
        </button>
      </>
    ) : (
      <>
        <button
          className="wol-btn"
          onClick={handleWakeOnLAN}
          disabled={isLoading || !pcInstance.mac_address}
        >
          {isLoading ? 'Bezig...' : 'Zet Computer Aan'}
        </button>
        {!pcInstance.mac_address && <div className="wol-error">Geen MAC adres beschikbaar</div>}
      </>
    );

    let icon = '💤';
    let message = 'Computer staat uit.';
    let messageClass = 'offline-message';
    let tileClass = 'pc-tile offline-tile';
    let showWake = true;
    switch (kind) {
      case 'reconnecting':
        icon = '🔄';
        // The auto-repair reboot's first seconds classify as a blip until the
        // DB status flips to 'restarting' — keep the countdown story coherent.
        message = autoRepairLive
          ? `Automatisch herstel — PC wordt herstart (${autoRepairCountdownText()})`
          : 'Verbinding maken…';
        tileClass += ' reconnecting-tile';
        // Wake stays offered here on purpose. This state means "we don't know
        // yet", and a magic packet is idempotent and harmless to a PC that is
        // actually up — so guessing wrong must never be what blocks starting a
        // room. The restarting/updating states below still hide it, because
        // those are a stated intent with a timeout rather than a guess.
        showWake = true;
        break;
      case 'restarting':
        icon = '🔄';
        message = autoRepairLive
          ? `Automatisch herstel — PC wordt herstart (${autoRepairCountdownText()})`
          : selfRepair
            ? 'Rebooting (self-repair) — back in ~2 min'
            : agentRestart
              ? 'Agent wordt herstart…'
              : 'PC wordt herstart…';
        messageClass += selfRepair || autoRepairLive ? ' diag-rebooting-message' : '';
        tileClass += ' diag-rebooting-tile';
        showWake = false;
        break;
      case 'updating':
        icon = '⬇️';
        message = 'Software bijwerken…';
        tileClass += ' updating-tile';
        showWake = false;
        break;
      case 'unreachable':
        icon = '📡';
        message = 'Niet bereikbaar…';
        tileClass += ' unreachable-tile';
        showWake = true;
        break;
      case 'off':
      default:
        icon = '💤';
        message = 'Computer staat uit.';
        showWake = true;
        break;
    }

    return (
      <div className={tileClass}>
        {renderMarkIssueExtras()}
        {renderHeader()}
        <div className="pc-tile-body">
          <div className="offline-content">
            <div className="offline-icon">{icon}</div>
            {showWake ? (
              <>
                {!wakeRequested && <div className={messageClass}>{message}</div>}
                {wakeRow}
              </>
            ) : (
              <div className={messageClass}>{message}</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Diagnostics states that take over the whole tile (accent + body). The
  // report_only flavor of malfunctioning intentionally does NOT — it keeps
  // the normal tile with a loud badge (launch stays allowed).
  const diagBlockedMalfunctioning = diagState === 'malfunctioning' && diagnostics?.enforcement === 'block';
  const diagRunActive = diagState === 'testing' || diagState === 'fixing';
  const diagTileClass = diagState === 'testing'
    ? ' diag-testing'
    : diagState === 'fixing'
      ? ' diag-fixing'
      : diagBlockedMalfunctioning
        ? ' diag-malfunctioning'
        : '';
  // Sync takeover accent — diagnostics accents always win; a running
  // experience outranks the takeover in the body ternary below anyway.
  const syncTileClass = !diagTileClass && syncBlockedTakeover && !currentExperience ? ' sync-syncing' : '';
  // Auto-repair countdown accent (reuses the diag "fixing" amber styling).
  const autoRepairTileClass = autoRepairLive && !diagTileClass ? ' diag-fixing' : '';
  // Visitor reported finished: optionally flash the whole tile until the
  // experience closes (the agent clears the notification on experience end, so
  // this stops on its own). Opt-in per device via settings.
  const visitorDoneBlinkClass =
    settings.visitorDoneBlinkEnabled &&
    notifications.some((n) => n.id === VISITOR_DONE_NOTIFICATION_ID)
      ? ' visitor-done-blink'
      : '';

  return (
    <div className={`pc-tile online ${dangerZone ? 'danger-zone-active' : ''} ${(!picoStatus?.connected || !headsetIsCharged) && !dangerZone && !diagTileClass && !syncTileClass ? 'devices-warning' : ''}${diagTileClass}${syncTileClass}${autoRepairTileClass}${visitorDoneBlinkClass}`}>
      {/* Red Button Popup */}
      {renderRedPopup()}

      {/* Mark Issue Modal + Toast */}
      {renderMarkIssueExtras()}

      {/* Diagnostics Override + Report Modals */}
      {renderDiagModals()}

      {/* Header */}
      {renderHeader()}

      {/* Diagnostics chips/badges (passed, degraded, pending HMD, override, report_only alarm) */}
      {renderDiagChips()}

      {/* Sync chips (sticky PAUSED badge, report_only syncing chip) */}
      {renderSyncChips()}

      {/* Notifications (compact) */}
      {notifications.length > 0 && (
        <div className="notifications-container-v2">
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className={`notification-v2 notification-${notification.type}${
                notification.id === VISITOR_DONE_NOTIFICATION_ID ? ' notification-visitor-done' : ''
              }`}
            >
              <span className="notification-icon-v2">
                {notification.id === VISITOR_DONE_NOTIFICATION_ID
                  ? '🚪'
                  : notification.type === 'error'
                    ? '⚠️'
                    : notification.type === 'warning'
                      ? '⚠'
                      : 'ℹ️'}
              </span>
              <span className="notification-text-v2">
                {crashView && CRASH_NOTIFICATION_IDS.includes(notification.id)
                  ? crashView.text
                  : notification.text}
              </span>
              {renderNotificationActions(notification)}
            </div>
          ))}
        </div>
      )}

      {/* Danger Zone Alert */}
      {dangerZone && notifications.length === 0 && (
        <div className="danger-zone-alert-v2">
          <span>⚠️ {dangerZoneReason || 'WAARSCHUWING'}</span>
        </div>
      )}

      {/* Body */}
      <div className="pc-tile-body">
        {/* Admin Info (compact) */}
        {isAdmin && (
          <div className="admin-info-compact">
            <div className="admin-info-row">
              <span className="admin-label-compact">IP:</span>
              <span className="admin-value-compact">{agentClient?.getActiveHost() || pcIP || 'N/A'}</span>
              <span className="admin-label-compact">v{pcInstance.agent_version || '?'}</span>
              {pcInstance.is_dedicated_server && (
                <span className="role-badge server">SERVER</span>
              )}
              {mpConnected !== undefined && (
                <span className={`mp-dot ${mpConnected ? 'connected' : 'disconnected'}`} title={`MP: ${mpConnected ? 'Connected' : 'Disconnected'}${mpServerIP ? ` (${mpServerIP})` : ''}`} />
              )}
              {mpShadowRelayed && (
                <span className="mp-shadow-badge" title="De game stuurt geen multiplayer-posities; de andere spelers zien deze bezoeker via de headset-schaduw.">
                  Speler zichtbaar via headset
                </span>
              )}
            </div>
            <div className="admin-info-row">
              <button
                className="admin-compact-btn update-btn"
                onClick={handleTriggerUpdate}
                disabled={isLoading || (!updateAvailable && !forceUpdate)}
                title={!updateAvailable && !forceUpdate ? 'Already on latest version' : ''}
              >
                {forceUpdate ? 'Force Update' : 'Update'}
              </button>
              <label className="force-update-label-compact">
                <input
                  type="checkbox"
                  checked={forceUpdate}
                  onChange={(e) => setForceUpdate(e.target.checked)}
                />
                <span>Force</span>
              </label>
              {/* Manual trigger — with auto_run gated off agent-side, this is
                  often the ONLY way a run ever starts. Shown for every state
                  except 'off' (and hidden entirely for old agents without a
                  diagnostics block); disabled while a run is active. The
                  agent's 409s (run_active/experience_running) surface as
                  toasts in handleRunDiagnostics. */}
              {diagnostics && diagState !== 'off' && (
                <>
                  <button
                    className="admin-compact-btn"
                    onClick={handleRunDiagnostics}
                    disabled={isLoading || diagRunActive || diagState === 'rebooting' || !!currentExperience}
                    title="Run the agent self-test cycle"
                  >
                    Run checks
                  </button>
                  {/* The report is readable in ANY state with run data —
                      including mid-run (in-progress detail) — so this is
                      never disabled. */}
                  <button
                    className="admin-compact-btn"
                    onClick={() => setShowDiagReport(true)}
                    title="View the diagnostics run report"
                  >
                    Report
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Experience States */}
        {autoRepairLive ? (
          /* Auto-repair (reboot-retry) in progress — countdown takeover */
          <div className="diag-run-body fixing">
            <div className="diag-run-title fixing">Automatisch herstel bezig</div>
            <div className="diag-run-subtitle">
              {autoRepair?.phase === 'testing'
                ? 'VR-stream wordt getest…'
                : 'PC wordt opnieuw opgestart…'}
            </div>
            <div className="diag-run-elapsed">{autoRepairCountdownText()}</div>
          </div>
        ) : diagRunActive ? (
          /* Diagnostics run in progress (testing/fixing) — launch UI hidden */
          renderDiagRunningBody()
        ) : currentExperience && !currentExperience.is_loading ? (
          /* Experience running */
          <div className="experience-running-v2">
            <div className="experience-running-info">
              <div className="experience-running-left">
                <div className="experience-name-v2">{currentExperience.name}</div>
                {currentExperience.scene_name && (
                  <div className="experience-scene-v2">
                    {currentExperience.current_level !== undefined
                      ? `${currentExperience.current_level + 1}${sceneCount ? `/${sceneCount}` : ''}: ${currentExperience.scene_name}`
                      : currentExperience.scene_name}
                  </div>
                )}
              </div>
              <div className="experience-running-right">
                <div className="time-display">
                  <span className="time-elapsed">{getElapsedTime()}</span>
                  <span className="time-separator"> / </span>
                  <span className="time-remaining">{getTimeRemaining()}</span>
                </div>
              </div>
            </div>
            <div className="mini-progress-bar">
              <div className="mini-progress-fill" style={{ width: `${getProgressPercentage()}%` }} />
            </div>

            {canControlLevels && (
              <button className="next-btn-large" onClick={handleNextLevel} disabled={isLoading}>
                NEXT
              </button>
            )}

            <div className="experience-running-actions">
              {canKillExperience && (
                <button className="stop-btn-v2" onClick={handleKillExperience} disabled={isLoading}>
                  STOP
                </button>
              )}
              {canControlLevels && (
                <button className="scene-select-btn-v2" onClick={handleGoToLevel} disabled={isLoading || loadingScenes}>
                  {loadingScenes ? 'Loading...' : 'SCENE SELECT'}
                </button>
              )}
            </div>

            {renderQueueVisitor()}

            {canInvalidateRun && (
              <button
                className="invalidate-btn-compact"
                onClick={handleInvalidateRun}
                disabled={isLoading}
                title="Delete this run from the database (won't be billed)"
              >
                Invalidate Run
              </button>
            )}
          </div>
        ) : currentExperience && currentExperience.is_loading ? (
          /* Experience loading */
          <div className="experience-loading-v2">
            <div className="experience-name-v2">{currentExperience.name}</div>
            <div className="launch-progress-container-v2">
              <div className="launch-progress-bar-v2">
                <div className="launch-progress-fill-v2" style={{ width: `${launchProgress}%` }} />
              </div>
              <span className="launch-progress-percent-v2">{Math.round(launchProgress)}%</span>
            </div>
            {canKillExperience && (
              <button className="stop-btn-v2" onClick={handleKillExperience} disabled={isLoading}>
                STOP
              </button>
            )}
            {renderQueueVisitor()}
          </div>
        ) : dispatchRecovering ? (
          /* Crashed: relaunch in flight, or the agent gave up on this visitor */
          renderCrashRecoveryBody()
        ) : diagBlockedMalfunctioning ? (
          /* Blocking checks failed — launch UI replaced by the failure summary */
          renderDiagMalfunctioningBody()
        ) : syncBlockedTakeover ? (
          /* Sync busy in block mode — launch grid becomes "Pauzeer sync & start" */
          renderSyncBody()
        ) : !(picoStatus?.connected && (headsetIsCharged || canLaunchWithoutHeadsetCharge)) && !isAdmin ? (
          /* Device connection warning (compact) */
          <div className="device-warning-compact">
            {!picoStatus?.connected && (
              <div className="device-warning-item">❌ Zet VR Bril aan</div>
            )}
            {picoStatus?.connected && (
              <div className="device-warning-item connected">✅ VR Bril</div>
            )}
            {!headsetIsCharged && (
              <div className="device-warning-item">❌ Koptelefoon opladen</div>
            )}
            {headsetIsCharged && (
              <div className="device-warning-item connected">✅ Koptelefoon</div>
            )}
          </div>
        ) : !checklistConfirmed && requiresChecklist ? (
          /* Pre-launch checklist (permission gated) */
          <div className="inline-checklist-v2">
            <div className="checklist-item-v2">✓ Veiligheidsuitleg gegeven?</div>
            <div className="checklist-item-v2">✓ Playspace correct?</div>
            <div className="checklist-item-v2">✓ IPD tussen 60-64?</div>
            <div className="checklist-item-v2">✓ Batterij 3+ bolletjes?</div>
            <div className="checklist-item-v2">✓ Lenzen schoon?</div>
            <div className="checklist-item-v2">✓ Koptelefoon aan?</div>
            <button className="confirm-checklist-btn-v2" onClick={handleConfirmChecklist} disabled={isLoading}>
              Klik hier om te starten
            </button>
          </div>
        ) : (
          /* Experience selection */
          <div className="experience-selection-v2">
            {/* Launch Progress */}
            {isLaunching && (
              <div className="launch-progress-container-v2">
                <div className="launch-progress-bar-v2">
                  <div className="launch-progress-fill-v2" style={{ width: `${launchProgress}%` }} />
                </div>
                <span className="launch-progress-percent-v2">{Math.round(launchProgress)}%</span>
              </div>
            )}

            {experiences.length > 0 ? (
              <>
                <div className={`experience-launch-grid-v2${experiences.length > 2 ? ' compact' : ''}`}>
                  {experiences.map((release: any) => {
                    // diagCanLaunch/syncCanLaunch each read ONLY their block's
                    // can_launch (absent block ⇒ old agent ⇒ allowed).
                    const canLaunch = (isAdmin || ((picoStatus?.connected || false) && (headsetIsCharged || canLaunchWithoutHeadsetCharge))) && diagCanLaunch && syncCanLaunch;
                    const disabled = isLoading || !canLaunch;
                    const supportedLanguages = release.experiences?.supported_languages || [];
                    const languageFlag = supportedLanguages.length === 1 ? supportedLanguages[0].language?.short_name : null;
                    const expColor = nameToColor(release.experiences?.name || 'Unknown');
                    const colorStyle = { '--btn-color': expColor.border, '--btn-bg': expColor.bg } as React.CSSProperties;

                    // One button per experience. Clicking launches directly when
                    // there's 0/1 language, opens the language picker modal for
                    // 2+ languages, or opens the multiplayer session modal for
                    // multiplayer/VC experiences (all handled in
                    // handleExperienceClick).
                    return (
                      <button
                        key={release.id}
                        className="launch-btn-v2"
                        style={colorStyle}
                        onClick={() => handleExperienceClick(release)}
                        disabled={disabled}
                      >
                        <span className="launch-btn-name-v2">
                          {release.experiences?.name || 'Unknown'}
                          {canViewReleaseDetails && release.experience_release?.version && (
                            <span className="version-tag">v{release.experience_release.version}</span>
                          )}
                          {canViewReleaseDetails && release.phase === 'beta' && <span className="phase-badge beta">BETA</span>}
                          {canViewReleaseDetails && release.phase === 'alpha' && <span className="phase-badge alpha">ALPHA</span>}
                          {languageFlag && <span className="language-flag-v2">{getLanguageFlag(languageFlag)}</span>}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {requiresChecklist && (
                  <button className="back-btn-v2" onClick={handleCancelLaunch} disabled={isLoading}>
                    ← Terug
                  </button>
                )}
              </>
            ) : (
              <p className="no-experiences-v2">No experiences available</p>
            )}
          </div>
        )}
      </div>

      {/* Scene Picker Modal */}
      {showScenePicker && (
        <div className="scene-picker-overlay" onClick={() => setShowScenePicker(false)}>
          <div className="scene-picker" onClick={(e) => e.stopPropagation()}>
            <div className="scene-picker-header">
              <span>Select Scene</span>
              <button className="scene-picker-close" onClick={() => setShowScenePicker(false)}>✕</button>
            </div>
            {scenes.length === 0 ? (
              <div className="scene-picker-empty">No scenes found.</div>
            ) : (
              <div className="scene-picker-list">
                {scenes.map((scene) => (
                  <button key={scene.index} className="scene-picker-item" onClick={() => handleSelectScene(scene)}>
                    {scene.index}: {scene.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Language Selection Modal */}
      {showLanguageSelect && selectedExperienceForLang && (
        <div className="language-selection-modal">
          <div className="language-selection-content">
            <h3>Select Language</h3>
            <p className="experience-name">{selectedExperienceForLang.experiences?.name}</p>
            <div className="language-buttons">
              {(selectedExperienceForLang.experiences?.supported_languages || []).map((langSupport: any) => (
                <button
                  key={langSupport.id}
                  className="language-btn"
                  onClick={() => {
                    handleLaunchExperience(
                      selectedExperienceForLang.experience_id,
                      selectedExperienceForLang.experience_release?.version || '1.0.0',
                      langSupport.language?.short_name || null
                    );
                  }}
                  disabled={isLoading}
                >
                  {hasLanguageFlag(langSupport.language?.short_name) ? (
                    <LanguageFlag code={langSupport.language?.short_name} className="language-flag-img-large" />
                  ) : (
                    <span className="language-flag-large">{getLanguageFlag(langSupport.language?.short_name || '')}</span>
                  )}
                  <span className="language-name">{langSupport.language?.name}</span>
                </button>
              ))}
            </div>
            <button
              className="cancel-lang-btn"
              onClick={() => { setShowLanguageSelect(false); setSelectedExperienceForLang(null); }}
              disabled={isLoading}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Volume Footer */}
      {renderVolumeFooter()}

      {/* Error Display */}
      {error && <div className="error-message-v2">{error}</div>}
    </div>
  );
}
